<template>
  <q-dialog
    :model-value="modelValue"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <q-card style="width: 540px; max-width: 95vw" class="manage-ps-dialog">
      <q-toolbar class="bg-blue-brand text-white">
        <q-toolbar-title>Pipeline Manager</q-toolbar-title>
        <q-btn v-close-popup flat round dense icon="close" />
      </q-toolbar>

      <q-tabs
        v-model="tab"
        dense
        class="text-grey"
        active-color="blue-brand"
        indicator-color="blue-brand"
        align="left"
        update-csv-file
        narrow-indicator
      >
        <q-tab name="create" label="Create New" />
        <q-tab name="update" label="Update Existing" />
      </q-tabs>

      <q-separator />

      <q-tab-panels v-model="tab" animated keep-alive>
        <!-- Create New Pipeline Section -->
        <q-tab-panel name="create">
          <div class="q-gutter-md" style="max-width: 500px">
            <q-input
              v-model="createName"
              dense
              outlined
              color="blue-brand"
              label="Name e.g., Oklahoma City to Tulsa"
              :error="createNameError !== ''"
              :error-message="createNameError"
            />

            <div class="row items-start q-gutter-sm">
              <q-input
                v-model="createDiameter"
                dense
                outlined
                color="blue-brand"
                :label="
                  'Diameter (' +
                  (createDiameterUnit === 'in' ? 'inches' : 'cm') +
                  ')'
                "
                :error="createDiameterError !== ''"
                :error-message="createDiameterError"
                :rules="[createDiameterRule]"
                reactive-rules
                class="col"
              />
              <q-select
                v-model="createDiameterUnit"
                dense
                outlined
                color="blue-brand"
                :options="[
                  { label: 'in', value: 'in' },
                  { label: 'cm', value: 'cm' },
                ]"
                emit-value
                map-options
                style="width: 90px"
              />
            </div>

            <!-- CSV mode question gate -->
            <div
              v-if="createCsvMode === null"
              class="q-pa-sm"
              style="border: 1px solid #e0e0e0; border-radius: 6px"
            >
              <div class="text-body2 text-grey-8 q-mb-sm">
                <q-icon name="help_outline" size="18px" class="q-mr-xs" />
                Do you have a properly formatted CSV file describing your
                pipeline?
              </div>
              <div class="row q-gutter-sm">
                <q-btn
                  outline
                  color="blue-brand"
                  label="Yes – upload CSV"
                  icon="upload_file"
                  size="sm"
                  no-caps
                  @click="createCsvMode = 'csv'"
                />
                <q-btn
                  outline
                  color="blue-brand"
                  label="No – build step by step"
                  icon="construction"
                  size="sm"
                  no-caps
                  @click="onOpenBuilder"
                />
              </div>
            </div>

            <!-- Change CSV mode link -->
            <div
              v-if="createCsvMode !== null && !createCsvFile"
              class="text-caption"
            >
              <a
                href="#"
                class="text-grey-7"
                style="text-decoration: underline"
                @click.prevent="onResetCsvMode"
              >
                &larr; Change how you provide marker data
              </a>
            </div>

            <!-- CSV upload path (shown after answering "Yes" or after builder completes) -->
            <template v-if="createCsvMode === 'csv'">
              <div
                class="csv-drop-zone"
                :class="{ 'csv-drop-zone--active': createDragOver }"
                @dragover.prevent="createDragOver = true"
                @dragenter.prevent="createDragOver = true"
                @dragleave.prevent="createDragOver = false"
                @drop.prevent="onDropCsv($event, 'create')"
              >
                <q-file
                  v-model="createCsvFile"
                  dense
                  outlined
                  color="blue-brand"
                  label="Survey Markers and Tracking Locations (CSV) File Upload"
                  accept=".csv"
                  clearable
                  :error="createCsvFileError !== ''"
                  :error-message="createCsvFileError"
                >
                  <template #prepend>
                    <q-icon name="attach_file" />
                  </template>
                  <template v-if="createCsvFile && createCsvValidation" #append>
                    <q-badge
                      v-if="createCsvValidation.errors.length > 0"
                      color="negative"
                      :label="
                        createCsvValidation.errors.length +
                        ' error' +
                        (createCsvValidation.errors.length > 1 ? 's' : '')
                      "
                      class="q-mr-xs"
                    />
                    <q-badge
                      v-if="createCsvValidation.warnings.length > 0"
                      color="amber-8"
                      :label="
                        createCsvValidation.warnings.length +
                        ' warning' +
                        (createCsvValidation.warnings.length > 1 ? 's' : '')
                      "
                    />
                  </template>
                </q-file>
                <div
                  v-if="!createCsvFile"
                  class="text-caption text-grey-6 text-center q-mt-xs"
                >
                  or drag &amp; drop a CSV file here
                </div>
              </div>

              <!-- Format hint (shown when no file selected) -->
              <div
                v-if="!createCsvFile"
                class="text-caption text-grey-7 q-mt-xs"
              >
                <div class="text-grey-8" style="font-size: 11.5px">
                  <q-icon name="info" size="14px" class="q-mr-xs" />
                  Valid header formats (click
                  <q-icon name="content_copy" size="12px" /> to copy):
                </div>
                <div class="q-mt-xs row items-center no-wrap">
                  <code class="text-grey-9" style="font-size: 11px; flex: 1">
                    AGM Marker Name,Latitude,Longitude,State,Description
                  </code>
                  <q-btn
                    flat
                    round
                    dense
                    size="xs"
                    icon="content_copy"
                    color="grey-7"
                    title="Copy header to clipboard"
                    class="q-ml-xs"
                    @click="
                      onCopyHeaderText(
                        'AGM Marker Name,Latitude,Longitude,State,Description'
                      )
                    "
                  />
                </div>
                <div class="q-mt-xs row items-center no-wrap">
                  <code class="text-grey-9" style="font-size: 11px; flex: 1">
                    AGM Marker Name,Latitude,Longitude,Pipeline Distance
                    (Meters),State,Description
                  </code>
                  <q-btn
                    flat
                    round
                    dense
                    size="xs"
                    icon="content_copy"
                    color="grey-7"
                    title="Copy header to clipboard"
                    class="q-ml-xs"
                    @click="
                      onCopyHeaderText(
                        'AGM Marker Name,Latitude,Longitude,Pipeline Distance (Meters),State,Description'
                      )
                    "
                  />
                </div>
                <div class="q-mt-xs row items-center no-wrap">
                  <code class="text-grey-9" style="font-size: 11px; flex: 1">
                    AGM Marker Name,Latitude,Longitude,Pipeline Distance
                    (Feet),State,Description
                  </code>
                  <q-btn
                    flat
                    round
                    dense
                    size="xs"
                    icon="content_copy"
                    color="grey-7"
                    title="Copy header to clipboard"
                    class="q-ml-xs"
                    @click="
                      onCopyHeaderText(
                        'AGM Marker Name,Latitude,Longitude,Pipeline Distance (Feet),State,Description'
                      )
                    "
                  />
                </div>
                <div class="q-mt-xs row items-center no-wrap">
                  <code class="text-grey-9" style="font-size: 11px; flex: 1">
                    AGM Marker Name,Latitude,Longitude,Pipeline Distance
                    (Miles),State,Description
                  </code>
                  <q-btn
                    flat
                    round
                    dense
                    size="xs"
                    icon="content_copy"
                    color="grey-7"
                    title="Copy header to clipboard"
                    class="q-ml-xs"
                    @click="
                      onCopyHeaderText(
                        'AGM Marker Name,Latitude,Longitude,Pipeline Distance (Miles),State,Description'
                      )
                    "
                  />
                </div>
                <div class="q-mt-sm text-caption text-grey-7">
                  Hover over each to see descriptive help:
                  <span class="cursor-pointer text-primary">
                    State<q-tooltip
                      class="text-body2"
                      anchor="top middle"
                      self="bottom middle"
                      max-width="220px"
                    >
                      Valid values: TRACK, SKIP
                    </q-tooltip></span
                  >,
                  <span class="cursor-pointer text-primary">
                    Description<q-tooltip
                      class="text-body2"
                      anchor="top middle"
                      self="bottom middle"
                      max-width="280px"
                    >
                      Valid values: AGM, ATTACHMENT, BEND, LAUNCH, MAGNET,
                      OTHER, PIPE, RECEIVE, TAP, TEE, VALVE, WELD
                    </q-tooltip></span
                  >,
                  <span class="cursor-pointer text-primary">
                    Pipeline Distance
                    <q-tooltip
                      class="text-body2"
                      anchor="top middle"
                      self="bottom middle"
                      max-width="300px"
                    >
                      Use column header "Pipeline Distance (Meters)", "Pipeline
                      Distance (Feet)", or "Pipeline Distance (Miles)". Start at
                      0 for the Launch row, and increase for each subsequent row
                      since it represents the cumulative distance along the
                      pipeline.
                    </q-tooltip>
                  </span>
                </div>
                <div class="q-mt-xs">
                  <a
                    href="#"
                    class="text-primary text-caption"
                    style="text-decoration: underline"
                    @click.prevent="onDownloadExampleCsv"
                    >Download example CSV</a
                  >
                </div>
              </div>
              <div v-if="createCsvFile && createCsvValidation" class="q-mt-xs">
                <div v-if="createCsvValidation.errors.length > 0">
                  <div
                    v-for="(err, idx) in showAllCreateErrors
                      ? createCsvValidation.errors
                      : createCsvValidation.errors.slice(0, 3)"
                    :key="'ce-' + idx"
                    class="text-caption text-negative row items-start no-wrap q-mb-xs"
                    :style="hasRowRef(err) ? { cursor: 'pointer' } : {}"
                    @click="scrollToErrorRow('createPreviewTable', err)"
                  >
                    <q-icon
                      name="error"
                      size="16px"
                      class="q-mr-xs"
                      color="negative"
                      style="flex-shrink: 0; margin-top: 1px"
                    />
                    <span>{{ err }}</span>
                  </div>
                  <a
                    v-if="
                      createCsvValidation.errors.length > 3 &&
                      !showAllCreateErrors
                    "
                    href="#"
                    class="text-caption text-primary"
                    @click.prevent="showAllCreateErrors = true"
                  >
                    Show {{ createCsvValidation.errors.length - 3 }} more
                    error(s)
                  </a>
                  <a
                    v-if="
                      createCsvValidation.errors.length > 3 &&
                      showAllCreateErrors
                    "
                    href="#"
                    class="text-caption text-primary"
                    @click.prevent="showAllCreateErrors = false"
                  >
                    Show fewer errors
                  </a>
                </div>
                <div v-if="createCsvValidation.warnings.length > 0">
                  <div
                    v-for="(warn, idx) in createCsvValidation.warnings"
                    :key="'cw-' + idx"
                    class="text-caption text-amber-9 row items-start no-wrap q-mb-xs"
                  >
                    <q-icon
                      name="warning"
                      size="16px"
                      class="q-mr-xs"
                      color="amber-8"
                      style="flex-shrink: 0; margin-top: 1px"
                    />
                    <span>{{ warn }}</span>
                  </div>
                </div>
                <div
                  v-if="createCsvValidation.valid"
                  class="text-caption text-positive row items-center no-wrap"
                >
                  <q-icon
                    name="check_circle"
                    size="16px"
                    class="q-mr-xs"
                    color="positive"
                  />
                  <span
                    >{{ createCsvValidation.parsedRows.length }} data rows,
                    {{ createCsvValidation.markerCount }} markers parsed &mdash;
                    {{ createCsvValidation.firstMarker }} &rarr;
                    {{ createCsvValidation.lastMarker
                    }}<span v-if="createCsvValidation.skipCount > 0">
                      &middot; {{ createCsvValidation.skipCount }} SKIP</span
                    ></span
                  >
                </div>
              </div>

              <!-- Parsed data table preview -->
              <q-table
                v-if="
                  createCsvFile &&
                  createCsvValidation &&
                  createCsvValidation.parsedRows.length > 0
                "
                ref="createPreviewTable"
                :rows="createCsvValidation.parsedRows"
                :columns="csvPreviewColumns"
                row-key="row"
                flat
                bordered
                dense
                hide-bottom
                :rows-per-page-options="[0]"
                class="q-mt-sm csv-preview-table"
                :style="{ maxHeight: '200px', overflowY: 'auto' }"
              >
                <template #body="props">
                  <q-tr :props="props" :data-row="props.row.row">
                    <q-td
                      v-for="col in props.cols"
                      :key="col.name"
                      :props="props"
                      :class="{
                        'bg-red-1': props.row.errorFields.includes(col.name),
                      }"
                    >
                      {{ col.value }}
                    </q-td>
                  </q-tr>
                </template>
              </q-table>

              <div>
                <q-btn
                  class="btn-brand"
                  label="Create"
                  :loading="loading"
                  :disable="
                    !!(
                      createCsvFile &&
                      (!createCsvValidation || !createCsvValidation.valid)
                    )
                  "
                  @click="onCreateSubmit"
                />
              </div>

              <!-- Mini map preview for create -->
              <div v-show="createCsvFile" class="q-mt-md">
                <div class="text-caption text-grey-8 q-mb-xs">
                  Pipeline Preview
                </div>
                <div
                  ref="createMiniMap"
                  style="
                    height: 250px;
                    width: 100%;
                    border: 1px solid #ccc;
                    border-radius: 4px;
                  "
                />
              </div>
            </template>
          </div>
        </q-tab-panel>

        <!-- Update Existing Pipeline Section -->
        <q-tab-panel name="update">
          <div class="q-gutter-md" style="max-width: 500px">
            <q-select
              v-model="selectedPipelineSection"
              dense
              outlined
              color="blue-brand"
              label="Name"
              :options="pipelineSectionOptions"
              :loading="loading"
              clearable
              @update:model-value="onPipelineSectionSelected"
              @clear="onPipelineSectionCleared"
            />

            <div class="row items-start q-gutter-sm">
              <q-input
                v-model="updateDiameter"
                dense
                outlined
                color="blue-brand"
                :label="
                  'Diameter (' +
                  (updateDiameterUnit === 'in' ? 'inches' : 'cm') +
                  ')'
                "
                :disable="!selectedPipelineSection"
                :rules="[(val) => !!val || 'Diameter is required.']"
                class="col"
              />
              <q-select
                v-model="updateDiameterUnit"
                dense
                outlined
                color="blue-brand"
                :options="[
                  { label: 'in', value: 'in' },
                  { label: 'cm', value: 'cm' },
                ]"
                emit-value
                map-options
                :disable="!selectedPipelineSection"
                style="width: 90px"
              />
            </div>

            <!-- Current tracking file -->
            <div v-if="selectedPipelineSection && currentTrackingFileName">
              <div class="text-caption text-grey-8 q-mb-xs">
                Current Tracking File
              </div>
              <div class="row items-center q-gutter-sm">
                <span class="text-body2">{{ currentTrackingFileName }}</span>
                <q-btn
                  flat
                  dense
                  size="sm"
                  icon="visibility"
                  color="blue-brand"
                  title="View file"
                  @click="onViewTrackingFile"
                />
                <q-btn
                  flat
                  dense
                  size="sm"
                  icon="download"
                  color="blue-brand"
                  title="Download file"
                  @click="onDownloadTrackingFile"
                />
              </div>
            </div>

            <div
              v-if="selectedPipelineSection && updateCsvMode === null"
              class="q-pa-sm"
              style="border: 1px solid #e0e0e0; border-radius: 6px"
            >
              <div class="text-body2 text-grey-8 q-mb-sm">
                <q-icon name="help_outline" size="18px" class="q-mr-xs" />
                Do you have a properly formatted CSV file describing your
                replacement pipeline markers?
              </div>
              <div class="row q-gutter-sm">
                <q-btn
                  outline
                  color="blue-brand"
                  label="Yes – upload CSV"
                  icon="upload_file"
                  size="sm"
                  no-caps
                  @click="updateCsvMode = 'csv'"
                />
                <q-btn
                  outline
                  color="blue-brand"
                  label="No – build step by step"
                  icon="construction"
                  size="sm"
                  no-caps
                  @click="onOpenUpdateBuilder"
                />
              </div>
            </div>

            <div
              v-if="updateCsvMode !== null && !updateCsvFile"
              class="text-caption"
            >
              <a
                href="#"
                class="text-grey-7"
                style="text-decoration: underline"
                @click.prevent="onResetUpdateCsvMode"
              >
                &larr; Change how you provide marker data
              </a>
            </div>

            <template v-if="updateCsvMode === 'csv'">
              <div
                class="csv-drop-zone"
                :class="{
                  'csv-drop-zone--active':
                    updateDragOver && selectedPipelineSection,
                }"
                @dragover.prevent="updateDragOver = true"
                @dragenter.prevent="updateDragOver = true"
                @dragleave.prevent="updateDragOver = false"
                @drop.prevent="onDropCsv($event, 'update')"
              >
                <q-file
                  v-model="updateCsvFile"
                  dense
                  outlined
                  color="blue-brand"
                  label="Survey Markers and Tracking Locations (CSV) File Upload"
                  accept=".csv"
                  clearable
                  :disable="!selectedPipelineSection"
                >
                  <template #prepend>
                    <q-icon name="attach_file" />
                  </template>
                  <template v-if="updateCsvFile && updateCsvValidation" #append>
                    <q-badge
                      v-if="updateCsvValidation.errors.length > 0"
                      color="negative"
                      :label="
                        updateCsvValidation.errors.length +
                        ' error' +
                        (updateCsvValidation.errors.length > 1 ? 's' : '')
                      "
                      class="q-mr-xs"
                    />
                    <q-badge
                      v-if="updateCsvValidation.warnings.length > 0"
                      color="amber-8"
                      :label="
                        updateCsvValidation.warnings.length +
                        ' warning' +
                        (updateCsvValidation.warnings.length > 1 ? 's' : '')
                      "
                    />
                  </template>
                </q-file>
                <div
                  v-if="!updateCsvFile && selectedPipelineSection"
                  class="text-caption text-grey-6 text-center q-mt-xs"
                >
                  or drag &amp; drop a CSV file here
                </div>
              </div>

              <!-- Format hint (shown when no replacement file selected) -->
              <div
                v-if="!updateCsvFile && selectedPipelineSection"
                class="text-caption text-grey-7 q-mt-xs"
              >
                <div class="text-grey-8" style="font-size: 11.5px">
                  <q-icon name="info" size="14px" class="q-mr-xs" />
                  Valid header formats (click
                  <q-icon name="content_copy" size="12px" /> to copy):
                </div>
                <div class="q-mt-xs row items-center no-wrap">
                  <code class="text-grey-9" style="font-size: 11px; flex: 1">
                    AGM Marker Name,Latitude,Longitude,State,Description
                  </code>
                  <q-btn
                    flat
                    round
                    dense
                    size="xs"
                    icon="content_copy"
                    color="grey-7"
                    title="Copy header to clipboard"
                    class="q-ml-xs"
                    @click="
                      onCopyHeaderText(
                        'AGM Marker Name,Latitude,Longitude,State,Description'
                      )
                    "
                  />
                </div>
                <div class="q-mt-xs row items-center no-wrap">
                  <code class="text-grey-9" style="font-size: 11px; flex: 1">
                    AGM Marker Name,Latitude,Longitude,Pipeline Distance
                    (Meters),State,Description
                  </code>
                  <q-btn
                    flat
                    round
                    dense
                    size="xs"
                    icon="content_copy"
                    color="grey-7"
                    title="Copy header to clipboard"
                    class="q-ml-xs"
                    @click="
                      onCopyHeaderText(
                        'AGM Marker Name,Latitude,Longitude,Pipeline Distance (Meters),State,Description'
                      )
                    "
                  />
                </div>
                <div class="q-mt-xs row items-center no-wrap">
                  <code class="text-grey-9" style="font-size: 11px; flex: 1">
                    AGM Marker Name,Latitude,Longitude,Pipeline Distance
                    (Feet),State,Description
                  </code>
                  <q-btn
                    flat
                    round
                    dense
                    size="xs"
                    icon="content_copy"
                    color="grey-7"
                    title="Copy header to clipboard"
                    class="q-ml-xs"
                    @click="
                      onCopyHeaderText(
                        'AGM Marker Name,Latitude,Longitude,Pipeline Distance (Feet),State,Description'
                      )
                    "
                  />
                </div>
                <div class="q-mt-xs row items-center no-wrap">
                  <code class="text-grey-9" style="font-size: 11px; flex: 1">
                    AGM Marker Name,Latitude,Longitude,Pipeline Distance
                    (Miles),State,Description
                  </code>
                  <q-btn
                    flat
                    round
                    dense
                    size="xs"
                    icon="content_copy"
                    color="grey-7"
                    title="Copy header to clipboard"
                    class="q-ml-xs"
                    @click="
                      onCopyHeaderText(
                        'AGM Marker Name,Latitude,Longitude,Pipeline Distance (Miles),State,Description'
                      )
                    "
                  />
                </div>
                <div class="q-mt-sm text-caption text-grey-7">
                  Hover over each to see descriptive help:
                  <span class="cursor-pointer text-primary">
                    State<q-tooltip
                      class="text-body2"
                      anchor="top middle"
                      self="bottom middle"
                      max-width="220px"
                    >
                      Valid values: TRACK, SKIP
                    </q-tooltip></span
                  >,
                  <span class="cursor-pointer text-primary">
                    Description<q-tooltip
                      class="text-body2"
                      anchor="top middle"
                      self="bottom middle"
                      max-width="280px"
                    >
                      Valid values: AGM, ATTACHMENT, BEND, LAUNCH, MAGNET,
                      OTHER, PIPE, RECEIVE, TAP, TEE, VALVE, WELD
                    </q-tooltip></span
                  >,
                  <span class="cursor-pointer text-primary">
                    Pipeline Distance
                    <q-tooltip
                      class="text-body2"
                      anchor="top middle"
                      self="bottom middle"
                      max-width="300px"
                    >
                      Use column header "Pipeline Distance (Meters)", "Pipeline
                      Distance (Feet)", or "Pipeline Distance (Miles)". Start at
                      0 for the Launch row, and increase for each subsequent row
                      since it represents the cumulative distance along the
                      pipeline.
                    </q-tooltip>
                  </span>
                </div>
                <div class="q-mt-xs">
                  <a
                    href="#"
                    class="text-primary text-caption"
                    style="text-decoration: underline"
                    @click.prevent="onDownloadExampleCsv"
                    >Download example CSV</a
                  >
                </div>
              </div>

              <!-- Validation feedback for update CSV -->
              <div v-if="updateCsvFile && updateCsvValidation" class="q-mt-xs">
                <div v-if="updateCsvValidation.errors.length > 0">
                  <div
                    v-for="(err, idx) in showAllUpdateErrors
                      ? updateCsvValidation.errors
                      : updateCsvValidation.errors.slice(0, 3)"
                    :key="'ue-' + idx"
                    class="text-caption text-negative row items-start no-wrap q-mb-xs"
                    :style="hasRowRef(err) ? { cursor: 'pointer' } : {}"
                    @click="scrollToErrorRow('updatePreviewTable', err)"
                  >
                    <q-icon
                      name="error"
                      size="16px"
                      class="q-mr-xs"
                      color="negative"
                      style="flex-shrink: 0; margin-top: 1px"
                    />
                    <span>{{ err }}</span>
                  </div>
                  <a
                    v-if="
                      updateCsvValidation.errors.length > 3 &&
                      !showAllUpdateErrors
                    "
                    href="#"
                    class="text-caption text-primary"
                    @click.prevent="showAllUpdateErrors = true"
                  >
                    Show {{ updateCsvValidation.errors.length - 3 }} more
                    error(s)
                  </a>
                  <a
                    v-if="
                      updateCsvValidation.errors.length > 3 &&
                      showAllUpdateErrors
                    "
                    href="#"
                    class="text-caption text-primary"
                    @click.prevent="showAllUpdateErrors = false"
                  >
                    Show fewer errors
                  </a>
                </div>
                <div v-if="updateCsvValidation.warnings.length > 0">
                  <div
                    v-for="(warn, idx) in updateCsvValidation.warnings"
                    :key="'uw-' + idx"
                    class="text-caption text-amber-9 row items-start no-wrap q-mb-xs"
                  >
                    <q-icon
                      name="warning"
                      size="16px"
                      class="q-mr-xs"
                      color="amber-8"
                      style="flex-shrink: 0; margin-top: 1px"
                    />
                    <span>{{ warn }}</span>
                  </div>
                </div>
                <div
                  v-if="updateCsvValidation.valid"
                  class="text-caption text-positive row items-center no-wrap"
                >
                  <q-icon
                    name="check_circle"
                    size="16px"
                    class="q-mr-xs"
                    color="positive"
                  />
                  <span
                    >{{ updateCsvValidation.parsedRows.length }} data rows,
                    {{ updateCsvValidation.markerCount }} markers parsed &mdash;
                    {{ updateCsvValidation.firstMarker }} &rarr;
                    {{ updateCsvValidation.lastMarker
                    }}<span v-if="updateCsvValidation.skipCount > 0">
                      &middot; {{ updateCsvValidation.skipCount }} SKIP</span
                    ></span
                  >
                </div>
              </div>

              <!-- Parsed data table preview -->
              <q-table
                v-if="
                  updateCsvFile &&
                  updateCsvValidation &&
                  updateCsvValidation.parsedRows.length > 0
                "
                ref="updatePreviewTable"
                :rows="updateCsvValidation.parsedRows"
                :columns="csvPreviewColumns"
                row-key="row"
                flat
                bordered
                dense
                hide-bottom
                :rows-per-page-options="[0]"
                class="q-mt-sm csv-preview-table"
                :style="{ maxHeight: '200px', overflowY: 'auto' }"
              >
                <template #body="props">
                  <q-tr :props="props" :data-row="props.row.row">
                    <q-td
                      v-for="col in props.cols"
                      :key="col.name"
                      :props="props"
                      :class="{
                        'bg-red-1': props.row.errorFields.includes(col.name),
                      }"
                    >
                      {{ col.value }}
                    </q-td>
                  </q-tr>
                </template>
              </q-table>
            </template>

            <div class="row q-gutter-sm">
              <q-btn
                class="btn-brand"
                label="Update"
                :disable="
                  !selectedPipelineSection ||
                  updateAction !== null ||
                  updateCsvMode === 'builder' ||
                  !!(
                    updateCsvFile &&
                    (!updateCsvValidation || !updateCsvValidation.valid)
                  )
                "
                :loading="updateAction === 'update'"
                @click="onUpdateSubmit"
              />
              <q-btn
                color="negative"
                label="Delete"
                :disable="!selectedPipelineSection || updateAction !== null"
                :loading="updateAction === 'delete'"
                @click="onDeleteSubmit"
              />
            </div>

            <!-- Mini map preview -->
            <div
              v-show="selectedPipelineSection && currentTrackingFile"
              class="q-mt-md"
            >
              <div class="text-caption text-grey-8 q-mb-xs">
                Pipeline Preview
              </div>
              <div
                ref="miniMap"
                style="
                  height: 250px;
                  width: 100%;
                  border: 1px solid #ccc;
                  border-radius: 4px;
                "
              />
            </div>
          </div>
        </q-tab-panel>
      </q-tab-panels>
    </q-card>

    <!-- View Tracking File Dialog -->
    <q-dialog
      v-model="viewTrackingFileDialog"
      maximized
      @hide="onViewTrackingFileDialogHide"
    >
      <q-card>
        <q-toolbar class="bg-blue-brand text-white">
          <q-toolbar-title>{{ currentTrackingFileName }}</q-toolbar-title>
          <q-btn
            flat
            round
            dense
            icon="download"
            title="Download"
            @click="onDownloadTrackingFile"
          />
          <q-btn v-close-popup flat round dense icon="close" />
        </q-toolbar>
        <q-card-section
          class="q-pa-md"
          style="overflow: auto; max-height: calc(100vh - 50px)"
        >
          <pre
            style="
              white-space: pre-wrap;
              word-break: break-all;
              font-size: 13px;
            "
            >{{ currentTrackingFile }}</pre
          >
        </q-card-section>
      </q-card>
    </q-dialog>
  </q-dialog>

  <!-- Pipeline Builder Wizard Dialog -->
  <q-dialog v-model="showBuilderDialog" persistent>
    <q-card style="width: 500px; max-width: 95vw">
      <q-toolbar class="bg-blue-brand text-white">
        <q-toolbar-title>{{ builderDialogTitle }}</q-toolbar-title>
        <q-btn flat round dense icon="close" @click="onBuilderCancel" />
      </q-toolbar>

      <q-card-section>
        <div class="text-caption text-grey-7 q-mb-sm">
          Step {{ builderStep }} of 5
        </div>

        <!-- Step 1: AGM Marker Names -->
        <div v-if="builderStep === 1">
          <div class="text-body2 q-mb-sm">
            Copy the AGM marker name column from your survey spreadsheet in
            Excel and paste it below. One name per row.
          </div>
          <q-input
            v-model="builderMarkerNamesRaw"
            type="textarea"
            outlined
            dense
            color="blue-brand"
            placeholder="Launch&#10;AGM-001&#10;AGM-002&#10;Receive"
            rows="10"
            @update:model-value="validateBuilderMarkerNames"
          />

          <!-- Validation feedback -->
          <div v-if="builderMarkerNameErrors.length > 0" class="q-mt-sm">
            <div
              v-for="(err, idx) in builderMarkerNameErrors"
              :key="'bne-' + idx"
              class="text-caption text-negative row items-start no-wrap q-mb-xs"
            >
              <q-icon
                name="error"
                size="16px"
                class="q-mr-xs"
                color="negative"
                style="flex-shrink: 0; margin-top: 1px"
              />
              <span>{{ err }}</span>
            </div>
          </div>

          <!-- Parsed count -->
          <div
            v-if="
              builderMarkerNames.length > 0 &&
              builderMarkerNameErrors.length === 0
            "
            class="text-caption text-positive q-mt-sm row items-center no-wrap"
          >
            <q-icon
              name="check_circle"
              size="16px"
              class="q-mr-xs"
              color="positive"
            />
            <span>{{ builderMarkerNames.length }} marker names entered</span>
          </div>
          <div
            v-else-if="
              builderMarkerNames.length > 0 &&
              builderMarkerNameErrors.length > 0
            "
            class="text-caption text-grey-7 q-mt-xs"
          >
            {{ builderMarkerNames.length }} name(s) parsed so far
          </div>
        </div>

        <!-- Step 2: Latitudes and Longitudes -->
        <div v-if="builderStep === 2">
          <div class="text-body2 q-mb-sm">
            Paste latitude and longitude columns separately. Row order must
            match the {{ builderMarkerNames.length }} marker names from the
            previous step. Each marker should be at least 2 m from the next
            (10&#43; m recommended).
          </div>
          <div class="text-caption text-grey-7 q-mb-xs">
            Expected: {{ builderMarkerNames.length }} coordinate pairs
            <span class="q-ml-sm">
              Latitudes: {{ builderLatitudeCount }} of
              {{ builderMarkerNames.length }} &middot; Longitudes:
              {{ builderLongitudeCount }} of {{ builderMarkerNames.length }}
            </span>
          </div>
          <div class="row q-gutter-sm">
            <div class="col">
              <q-input
                v-model="builderLatitudesRaw"
                type="textarea"
                outlined
                dense
                color="blue-brand"
                label="Latitudes"
                placeholder="35.4676&#10;35.4812&#10;35.4955&#10;35.5101"
                rows="10"
                @update:model-value="validateBuilderCoords"
              />
            </div>
            <div class="col">
              <q-input
                v-model="builderLongitudesRaw"
                type="textarea"
                outlined
                dense
                color="blue-brand"
                label="Longitudes"
                placeholder="-97.5164&#10;-97.5032&#10;-97.4898&#10;-97.4771"
                rows="10"
                @update:model-value="validateBuilderCoords"
              />
            </div>
          </div>

          <!-- Validation feedback -->
          <div v-if="builderCoordErrors.length > 0" class="q-mt-sm">
            <div
              v-for="(err, idx) in builderCoordErrors"
              :key="'bce-' + idx"
              class="text-caption text-negative row items-start no-wrap q-mb-xs"
            >
              <q-icon
                name="error"
                size="16px"
                class="q-mr-xs"
                color="negative"
                style="flex-shrink: 0; margin-top: 1px"
              />
              <span>{{ err }}</span>
            </div>
          </div>

          <!-- Success -->
          <div
            v-if="builderCoordErrors.length === 0 && builderCoordsValid"
            class="text-caption text-positive q-mt-sm row items-center no-wrap"
          >
            <q-icon
              name="check_circle"
              size="16px"
              class="q-mr-xs"
              color="positive"
            />
            <span
              >{{ builderMarkerNames.length }} coordinate pairs entered</span
            >
          </div>
        </div>

        <!-- Step 3: Track States -->
        <div v-if="builderStep === 3">
          <div class="text-body2 q-mb-sm">
            <div>
              AGM markers are tracked by default. Launch (first) and Receive
              (last) are skipped by default - check any marker you want tracked.
            </div>
          </div>
          <q-list bordered separator>
            <q-item
              v-for="(markerName, index) in builderMarkerNames"
              :key="markerName"
              dense
            >
              <q-item-section>
                <q-checkbox
                  v-model="builderTrackStates[index]"
                  :label="markerName"
                  true-value="T"
                  false-value="S"
                  color="blue-brand"
                />
              </q-item-section>
              <q-item-section side>
                <span
                  :class="
                    builderTrackStates[index] === 'T'
                      ? 'text-positive'
                      : 'text-grey-7'
                  "
                  class="text-caption"
                >
                  {{ builderTrackStates[index] === 'T' ? 'Track' : 'Skip' }}
                </span>
              </q-item-section>
            </q-item>
          </q-list>
        </div>

        <!-- Step 4: Optional Pipeline Distance -->
        <div v-if="builderStep === 4">
          <div class="text-body2 q-mb-sm">
            Optionally paste cumulative pipeline distances below, one value per
            marker row. Values must start at 0 and increase for each subsequent
            row.
          </div>
          <div class="row items-center q-gutter-sm q-mb-xs">
            <q-select
              v-model="builderDistanceUnit"
              dense
              outlined
              color="blue-brand"
              label="Distance unit"
              :options="[
                { label: 'Feet', value: 'feet' },
                { label: 'Meters', value: 'meters' },
                { label: 'Miles', value: 'miles' },
              ]"
              emit-value
              map-options
              style="width: 160px"
              @update:model-value="onBuilderDistanceUnitChange"
            />
            <q-btn
              flat
              dense
              color="blue-brand"
              label="Skip distance"
              no-caps
              @click="onSkipBuilderDistances"
            />
          </div>
          <div class="text-caption text-grey-7 q-mb-xs">
            Expected: {{ builderMarkerNames.length }}
            {{ builderDistanceUnit }} values &middot; Entered:
            {{ builderDistanceCount }} of
            {{ builderMarkerNames.length }}
          </div>
          <q-input
            v-model="builderDistancesRaw"
            type="textarea"
            outlined
            dense
            color="blue-brand"
            :label="`Cumulative Pipeline Distance (${
              builderDistanceUnit === 'feet'
                ? 'Feet'
                : builderDistanceUnit === 'miles'
                ? 'Miles'
                : 'Meters'
            })`"
            :placeholder="
              builderDistanceUnit === 'feet'
                ? '0\n250\n575\n910'
                : builderDistanceUnit === 'miles'
                ? '0\n0.047\n0.109\n0.172'
                : '0\n76.2\n175.3\n277.4'
            "
            rows="10"
            @update:model-value="onBuilderDistanceInput"
          />

          <div v-if="builderDistanceErrors.length > 0" class="q-mt-sm">
            <div
              v-for="(err, idx) in builderDistanceErrors"
              :key="'bde-' + idx"
              class="text-caption text-negative row items-start no-wrap q-mb-xs"
            >
              <q-icon
                name="error"
                size="16px"
                class="q-mr-xs"
                color="negative"
                style="flex-shrink: 0; margin-top: 1px"
              />
              <span>{{ err }}</span>
            </div>
          </div>

          <div
            v-else-if="builderDistanceSkipped"
            class="text-caption text-grey-7 q-mt-sm row items-center no-wrap"
          >
            <q-icon name="skip_next" size="16px" class="q-mr-xs" />
            <span>Pipeline distance will not be included.</span>
          </div>
          <div
            v-else-if="builderDistancesValid"
            class="text-caption text-positive q-mt-sm row items-center no-wrap"
          >
            <q-icon
              name="check_circle"
              size="16px"
              class="q-mr-xs"
              color="positive"
            />
            <span>{{ builderDistanceCount }} cumulative distances entered</span>
          </div>
        </div>

        <!-- Step 5: Preview Pipeline Coordinates -->
        <div v-if="builderStep === 5">
          <div class="text-body2 q-mb-sm">
            Scroll or pinch to zoom - drag to pan - tap a marker to toggle
            TRACK/SKIP. Click Finish to
            {{ builderOperation === 'update' ? 'update' : 'create' }} the
            pipeline section.
          </div>
          <div class="text-caption text-grey-8 q-mb-xs">Pipeline Preview</div>
          <div
            ref="builderMiniMap"
            style="
              height: 280px;
              width: 100%;
              border: 1px solid #ccc;
              border-radius: 4px;
            "
          />
        </div>
      </q-card-section>

      <q-separator />

      <q-card-actions align="right" class="q-pa-md">
        <q-btn
          flat
          label="Cancel"
          color="grey-7"
          no-caps
          @click="onBuilderCancel"
        />
        <q-btn
          v-if="builderStep > 1"
          flat
          label="Back"
          color="grey-7"
          no-caps
          @click="onBuilderBack"
        />
        <q-btn
          class="btn-brand"
          :label="builderStep < 5 ? 'Next' : 'Finish'"
          no-caps
          :disable="!builderStepValid || loading"
          :loading="loading"
          @click="onBuilderNext"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>

  <!-- Duplicate Marker Names Dialog -->
  <q-dialog v-model="showDuplicateMarkerDialog" persistent>
    <q-card style="width: 500px; max-width: 95vw">
      <q-card-section class="row items-center q-pb-none">
        <q-icon name="warning" color="warning" size="24px" class="q-mr-sm" />
        <div class="text-h6">Duplicate Marker Names</div>
      </q-card-section>
      <q-card-section>
        <div class="text-body2 q-mb-md">
          Each marker needs a unique name. We suggest numbering duplicates like
          VALVE -&gt; VALVE_1, VALVE_2:
        </div>
        <q-markup-table flat bordered dense>
          <thead>
            <tr>
              <th class="text-left">Row</th>
              <th class="text-left">Original</th>
              <th class="text-left">Proposed</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="change in duplicateMarkerNameChanges" :key="change.row">
              <td>{{ change.row }}</td>
              <td>{{ change.originalName }}</td>
              <td>{{ change.proposedName }}</td>
            </tr>
          </tbody>
        </q-markup-table>
        <div class="text-body2 q-mt-md">
          Apply these renames and continue, or go back and edit the names
          manually?
        </div>
      </q-card-section>
      <q-card-actions align="right" class="q-pa-md">
        <q-btn
          flat
          label="I'll edit manually"
          color="grey-7"
          no-caps
          @click="onRejectDuplicateMarkerNames"
        />
        <q-btn
          class="btn-brand"
          label="Rename Markers"
          no-caps
          @click="onAcceptDuplicateMarkerNames"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>

  <!-- Close Marker Coordinates Dialog -->
  <q-dialog v-model="showCloseMarkerWarningDialog" persistent>
    <q-card style="width: 500px; max-width: 95vw">
      <q-card-section class="row items-center q-pb-none">
        <q-icon name="warning" color="warning" size="24px" class="q-mr-sm" />
        <div class="text-h6">Markers too close together</div>
      </q-card-section>
      <q-card-section>
        <div class="text-body2 q-mb-md">
          Each marker needs a distinct position (at least 2 m apart). We suggest
          removing the later row of each close pair:
        </div>
        <q-list bordered separator dense>
          <q-item
            v-for="pair in closeMarkerPairs"
            :key="`${pair.earlierRow}-${pair.laterRow}`"
          >
            <q-item-section>
              Row {{ pair.laterRow }} "{{ pair.laterName }}" -
              {{ pair.meters.toFixed(1) }} m from row {{ pair.earlierRow }} "{{
                pair.earlierName
              }}"
            </q-item-section>
          </q-item>
        </q-list>
      </q-card-section>
      <q-card-actions align="right" class="q-pa-md">
        <q-btn
          flat
          label="I'll edit manually"
          color="grey-7"
          no-caps
          @click="onRejectCloseMarkerWarning"
        />
        <q-btn
          class="btn-brand"
          :label="closeMarkerWarningActionLabel"
          no-caps
          @click="onRemoveCloseMarkerRows"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>

  <InstructionCard
    v-model="showInstructions"
    title="Pipelines"
    icon="description"
    message="Create a pipeline by entering a name, diameter, and uploading a CSV file with your marker positions."
    tip="Download an example CSV to see the expected format."
    tip-action-label="Download example CSV"
    :z-index="7000"
    @tip-action="onDownloadExampleCsv"
  />
</template>

<script lang="ts">
import L from 'leaflet';
import 'leaflet-extra-markers/dist/css/leaflet.extra-markers.min.css';
import { defineComponent, markRaw } from 'vue';
import PipelineSectionService from '@/services/PipelineSectionService';
import InstructionCard from '@/components/InstructionCard.vue';
import { CreatePipelineRequest } from '@/models/PipelineSection/CreatePipelineRequest';
import { UpdatePipelineRequest } from '@/models/PipelineSection/UpdatePipelineRequest';
import { PipelineSection } from '@/models/PipelineSection/PipelineSection';
import PigRunCommon from '@/utils/PigRunCommon';

export default defineComponent({
  name: 'PipelineSectionManager',
  components: { InstructionCard },
  props: {
    modelValue: { type: Boolean, default: false },
  },
  emits: ['update:modelValue', 'sections-updated'],
  data() {
    return {
      tab: 'create',
      loading: false,

      // Create form state
      createName: '',
      createNameError: '',
      createDiameter: '',
      createDiameterError: '',
      createDiameterUnit: 'in' as 'in' | 'cm',
      createCsvFile: null as File | null,
      createCsvFileError: '',
      showAllCreateErrors: false,
      createDragOver: false,

      // Create CSV mode gate: null = show question, 'csv' = show file upload, 'builder' = wizard used
      createCsvMode: null as null | 'csv' | 'builder',

      // Builder wizard state
      showBuilderDialog: false,
      builderOperation: 'create' as 'create' | 'update',
      builderStep: 1,
      builderMarkerNamesRaw: '',
      builderMarkerNames: [] as string[],
      builderMarkerNameErrors: [] as string[],
      showDuplicateMarkerDialog: false,
      duplicateMarkerSource: null as null | 'builder' | 'create' | 'update',
      duplicateMarkerCsvContent: '',
      duplicateMarkerNameChanges: [] as {
        row: number;
        originalName: string;
        proposedName: string;
      }[],
      showCloseMarkerWarningDialog: false,
      closeMarkerSource: null as null | 'builder' | 'create' | 'update',
      closeMarkerCsvContent: '',
      closeMarkerPairs: [] as {
        earlierRow: number;
        laterRow: number;
        earlierName: string;
        laterName: string;
        meters: number;
      }[],
      builderLatitudesRaw: '',
      builderLongitudesRaw: '',
      builderCoordErrors: [] as string[],
      builderTrackStates: [] as ('T' | 'S')[],
      builderTrackStateMarkerNames: [] as string[],
      builderDistancesRaw: '',
      builderDistanceUnit: 'feet' as 'meters' | 'feet' | 'miles',
      builderDistanceErrors: [] as string[],
      builderDistanceSkipped: false,

      // Update form state
      selectedPipelineSection: null as any,
      updateDiameter: '',
      updateDiameterUnit: 'in' as 'in' | 'cm',
      updateCsvFile: null as File | null,
      updateCsvMode: null as null | 'csv' | 'builder',
      updateAction: null as null | 'update' | 'delete',
      showAllUpdateErrors: false,
      updateDragOver: false,
      currentTrackingFileName: '',
      currentTrackingFile: '',
      viewTrackingFileDialog: false,

      // Full pipeline section data from API
      pipelineSections: [] as PipelineSection[],

      // Map preview markers for CSV points (main map, used by view dialog)
      csvMapMarkers: [] as L.Marker[],
      csvPolyline: null as L.Polyline | null,

      // Mini map for update tab
      miniMap: null as L.Map | null,
      miniMapLayer: null as L.LayerGroup | null,

      // Mini map for create tab
      createMiniMap: null as L.Map | null,
      createMiniMapLayer: null as L.LayerGroup | null,

      // Mini map for builder wizard step 5
      builderMiniMap: null as L.Map | null,
      builderMiniMapLayer: null as L.LayerGroup | null,
      builderMiniMapMarkers: [] as (L.Marker | null)[],

      // Dropdown options populated from API
      pipelineSectionOptions: [] as any[],
      showInstructions: false,

      // CSV validation state
      createCsvValidation: null as {
        valid: boolean;
        errors: string[];
        warnings: string[];
        markerCount: number;
        firstMarker: string;
        lastMarker: string;
        skipCount: number;
        hasDistance: boolean;
        distanceUnit: 'meters' | 'feet' | 'miles' | null;
        closeMarkerPairs: {
          earlierRow: number;
          laterRow: number;
          earlierName: string;
          laterName: string;
          meters: number;
        }[];
        duplicateMarkerNameChanges: {
          row: number;
          originalName: string;
          proposedName: string;
        }[];
        parsedRows: {
          row: number;
          name: string;
          lat: string;
          lon: string;
          distance: string;
          state: string;
          description: string;
          hasError: boolean;
          errorFields: string[];
        }[];
      } | null,
      updateCsvValidation: null as {
        valid: boolean;
        errors: string[];
        warnings: string[];
        markerCount: number;
        firstMarker: string;
        lastMarker: string;
        skipCount: number;
        hasDistance: boolean;
        distanceUnit: 'meters' | 'feet' | 'miles' | null;
        closeMarkerPairs: {
          earlierRow: number;
          laterRow: number;
          earlierName: string;
          laterName: string;
          meters: number;
        }[];
        duplicateMarkerNameChanges: {
          row: number;
          originalName: string;
          proposedName: string;
        }[];
        parsedRows: {
          row: number;
          name: string;
          lat: string;
          lon: string;
          distance: string;
          state: string;
          description: string;
          hasError: boolean;
          errorFields: string[];
        }[];
      } | null,
    };
  },
  computed: {
    map(): any {
      return this.$store.state.map;
    },
    builderDialogTitle(): string {
      const stepTitles: Record<number, string> = {
        1: 'Specify Markers',
        2: 'Set Coordinates',
        3: 'Select Track Locations',
        4: 'Set Distance',
        5: 'Review',
      };
      const builderTitle =
        this.builderOperation === 'update'
          ? 'Update Pipeline Builder'
          : 'Pipeline Builder';
      return `${builderTitle} - ${
        stepTitles[this.builderStep] || 'Pipeline Builder'
      }`;
    },
    builderCoordsValid(): boolean {
      return (
        this.builderLatitudeCount === this.builderMarkerNames.length &&
        this.builderLongitudeCount === this.builderMarkerNames.length &&
        this.builderCoordErrors.length === 0
      );
    },
    builderLatitudeCount(): number {
      return this.builderLatitudesRaw
        .split('\n')
        .map((value: string) => value.trim())
        .filter((value: string) => value.length > 0).length;
    },
    builderLongitudeCount(): number {
      return this.builderLongitudesRaw
        .split('\n')
        .map((value: string) => value.trim())
        .filter((value: string) => value.length > 0).length;
    },
    builderDistanceCount(): number {
      return this.builderDistancesRaw
        .split('\n')
        .map((value: string) => value.trim())
        .filter((value: string) => value.length > 0).length;
    },
    builderDistancesValid(): boolean {
      return (
        this.builderDistanceSkipped ||
        (this.builderDistanceCount === this.builderMarkerNames.length &&
          this.builderDistanceErrors.length === 0)
      );
    },
    builderTrackStatesValid(): boolean {
      return (
        this.builderTrackStates.length === this.builderMarkerNames.length &&
        this.builderTrackStateMarkerNames.length ===
          this.builderMarkerNames.length &&
        this.builderTrackStates.every(
          (trackState) => trackState === 'T' || trackState === 'S'
        ) &&
        this.builderTrackStateMarkerNames.every(
          (markerName: string, index: number) =>
            markerName === this.builderMarkerNames[index]
        )
      );
    },
    builderStepValid(): boolean {
      if (this.builderStep === 1) {
        return (
          this.builderMarkerNames.length >= 2 &&
          this.builderMarkerNameErrors.length === 0
        );
      }
      if (this.builderStep === 2) {
        return this.builderCoordsValid;
      }
      if (this.builderStep === 3) {
        return this.builderTrackStatesValid;
      }
      if (this.builderStep === 4) {
        return this.builderDistancesValid;
      }
      if (this.builderStep === 5) {
        return this.builderCoordsValid && this.builderDistancesValid;
      }
      return false;
    },
    closeMarkerWarningActionLabel(): string {
      if (this.closeMarkerPairs.length === 1) {
        return `Remove row ${this.closeMarkerPairs[0].laterRow}`;
      }
      return 'Remove later rows';
    },
    csvPreviewColumns(): any[] {
      const showDist =
        this.createCsvValidation?.hasDistance ||
        this.updateCsvValidation?.hasDistance;
      const cols: any[] = [
        { name: 'row', label: 'Row', field: 'row', align: 'left' },
        {
          name: 'name',
          label: 'AGM Marker Name',
          field: 'name',
          align: 'left',
        },
        { name: 'lat', label: 'Latitude', field: 'lat', align: 'left' },
        { name: 'lon', label: 'Longitude', field: 'lon', align: 'left' },
      ];
      if (showDist) {
        const unit =
          this.createCsvValidation?.distanceUnit ||
          this.updateCsvValidation?.distanceUnit ||
          'meters';
        const unitLabel =
          unit === 'feet' ? 'ft' : unit === 'miles' ? 'mi' : 'm';
        cols.push({
          name: 'distance',
          label: `Distance (${unitLabel})`,
          field: 'distance',
          align: 'left',
        });
      }
      cols.push(
        { name: 'state', label: 'State', field: 'state', align: 'left' },
        {
          name: 'description',
          label: 'Description',
          field: 'description',
          align: 'left',
        }
      );
      return cols;
    },
  },
  watch: {
    modelValue(val: boolean) {
      if (val) {
        this.showInstructions = true;
        this.resetForm();
        this.loadPipelineSections();
      } else {
        this.showInstructions = false;
        this.destroyMiniMap();
        this.destroyCreateMiniMap();
        this.destroyBuilderMiniMap();
      }
    },
    createCsvFile(file: File | null) {
      if (!file) {
        this.createCsvValidation = null;
        this.showAllCreateErrors = false;
        this.clearCreateMiniMap();
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        if (content) {
          this.createCsvValidation = this.validateCsv(content);
          this.plotCsvPointsOnCreateMiniMap(content);
          this.maybeShowCsvDuplicateOrCloseWarning('create', content);
        }
      };
      reader.readAsText(file);
    },
    updateDiameterUnit(newVal: 'in' | 'cm', oldVal: 'in' | 'cm') {
      const val = parseFloat(this.updateDiameter);
      if (!isNaN(val) && val > 0) {
        const meters = oldVal === 'in' ? val * 0.0254 : val * 0.01;
        const converted = newVal === 'in' ? meters / 0.0254 : meters / 0.01;
        this.updateDiameter = String(parseFloat(converted.toFixed(4)));
      }
    },
    updateCsvFile(file: File | null) {
      if (!file) {
        this.updateCsvValidation = null;
        this.showAllUpdateErrors = false;
        if (this.currentTrackingFile) {
          this.$nextTick(() => {
            this.plotCsvPointsOnMiniMap(this.currentTrackingFile);
          });
        } else {
          this.clearMiniMap();
        }
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        if (content) {
          this.updateCsvValidation = this.validateCsv(content);
          this.plotCsvPointsOnMiniMap(content);
          this.maybeShowCsvDuplicateOrCloseWarning('update', content);
        }
      };
      reader.readAsText(file);
    },
  },
  methods: {
    onDownloadExampleCsv() {
      const link = document.createElement('a');
      link.href = `${process.env.BASE_URL || '/'}example.csv`;
      link.download = 'example.csv';
      link.click();
    },

    onCopyHeaderText(text: string) {
      navigator.clipboard.writeText(text).then(() => {
        this.$q.notify({
          color: 'positive',
          message: 'Header copied to clipboard.',
          timeout: 1500,
        });
      });
    },

    onResetCsvMode() {
      this.createCsvMode = null;
      this.createCsvFile = null;
      this.createCsvValidation = null;
      this.createCsvFileError = '';
    },

    onResetUpdateCsvMode() {
      this.updateCsvMode = null;
      this.updateCsvFile = null;
      this.updateCsvValidation = null;
      this.showAllUpdateErrors = false;
      this.updateDragOver = false;
      this.builderOperation = 'create';
    },

    validateCreateNameAndDiameter(): boolean {
      this.createNameError = '';
      this.createDiameterError = '';

      const name = this.createName;
      if (!name || name.trim().length === 0) {
        this.createNameError = 'Name is required.';
      } else if (name.trim().length > 500) {
        this.createNameError = 'Name must be 500 characters or less.';
      }

      const diameter = this.createDiameter;
      const minDiameterMeters = 0.1016;
      const maxDiameterMeters = 1.27;
      let diameterInMeters = 0;
      if (diameter === null || diameter === '') {
        this.createDiameterError = 'Diameter is required.';
      } else if (isNaN(parseFloat(diameter)) || parseFloat(diameter) <= 0) {
        this.createDiameterError = 'Diameter must be a positive number.';
      } else {
        diameterInMeters =
          this.createDiameterUnit === 'in'
            ? parseFloat(diameter) * 0.0254
            : parseFloat(diameter) * 0.01;
        if (
          diameterInMeters < minDiameterMeters ||
          diameterInMeters > maxDiameterMeters
        ) {
          this.createDiameterError =
            this.createDiameterUnit === 'in'
              ? 'Diameter must be between 4 and 50 inches.'
              : 'Diameter must be between 10.16 and 127 cm.';
        }
      }

      return !this.createNameError && !this.createDiameterError;
    },

    createPipelineRequest(
      trackingFileName: string,
      trackingFile: string
    ): CreatePipelineRequest {
      const diameterMeters =
        this.createDiameterUnit === 'in'
          ? parseFloat(this.createDiameter) * 0.0254
          : parseFloat(this.createDiameter) * 0.01;

      return new CreatePipelineRequest(
        this.createName.trim(),
        diameterMeters,
        trackingFileName,
        trackingFile
      );
    },

    getUpdateDiameterMeters(): number {
      return this.updateDiameterUnit === 'in'
        ? parseFloat(this.updateDiameter) * 0.0254
        : parseFloat(this.updateDiameter) * 0.01;
    },

    validateUpdateDiameter(): boolean {
      const diameterMeters = this.getUpdateDiameterMeters();
      if (
        isNaN(diameterMeters) ||
        diameterMeters < 0.1016 ||
        diameterMeters > 1.27
      ) {
        this.$q.notify({
          color: 'warning',
          message:
            this.updateDiameterUnit === 'in'
              ? 'Diameter must be between 4 and 50 inches.'
              : 'Diameter must be between 10.16 and 127 cm.',
        });
        return false;
      }
      return true;
    },

    getBuilderColumnValues(rawValues: string): string[] {
      return rawValues
        .split('\n')
        .map((value: string) => value.trim())
        .filter((value: string) => value.length > 0);
    },

    generateBuilderTrackingFile(): string {
      const latitudes = this.getBuilderColumnValues(this.builderLatitudesRaw);
      const longitudes = this.getBuilderColumnValues(this.builderLongitudesRaw);
      const distances = this.getBuilderColumnValues(this.builderDistancesRaw);
      const hasDistance = !this.builderDistanceSkipped;
      const header = hasDistance
        ? `AGM Marker Name,Latitude,Longitude,Pipeline Distance (${
            this.builderDistanceUnit === 'feet'
              ? 'Feet'
              : this.builderDistanceUnit === 'miles'
              ? 'Miles'
              : 'Meters'
          }),State,Description`
        : 'AGM Marker Name,Latitude,Longitude,State,Description';
      const rows = this.builderMarkerNames.map((markerName, index) => {
        const description =
          index === 0
            ? 'LAUNCH'
            : index === this.builderMarkerNames.length - 1
            ? 'RECEIVE'
            : 'AGM';
        const trackState =
          this.builderTrackStates[index] === 'T' ? 'TRACK' : 'SKIP';
        const values = [markerName, latitudes[index], longitudes[index]];

        if (hasDistance) {
          values.push(distances[index].replace(/,/g, ''));
        }

        values.push(trackState, description);
        return values.join(',');
      });

      return [header, ...rows].join('\n');
    },

    resetBuilderState() {
      this.destroyBuilderMiniMap();
      this.builderStep = 1;
      this.builderMarkerNamesRaw = '';
      this.builderMarkerNames = [];
      this.builderMarkerNameErrors = [];
      this.showDuplicateMarkerDialog = false;
      this.duplicateMarkerSource = null;
      this.duplicateMarkerCsvContent = '';
      this.duplicateMarkerNameChanges = [];
      this.showCloseMarkerWarningDialog = false;
      this.closeMarkerSource = null;
      this.closeMarkerCsvContent = '';
      this.closeMarkerPairs = [];
      this.builderLatitudesRaw = '';
      this.builderLongitudesRaw = '';
      this.builderCoordErrors = [];
      this.builderTrackStates = [];
      this.builderTrackStateMarkerNames = [];
      this.builderDistancesRaw = '';
      this.builderDistanceUnit = 'feet';
      this.builderDistanceErrors = [];
      this.builderDistanceSkipped = false;
    },

    onOpenBuilder() {
      if (!this.validateCreateNameAndDiameter()) {
        return;
      }

      this.createCsvMode = 'builder';
      this.builderOperation = 'create';
      this.resetBuilderState();
      this.showBuilderDialog = true;
    },

    onOpenUpdateBuilder() {
      if (!this.selectedPipelineSection || !this.validateUpdateDiameter()) {
        return;
      }

      this.updateCsvMode = 'builder';
      this.updateCsvFile = null;
      this.builderOperation = 'update';
      this.resetBuilderState();
      this.showBuilderDialog = true;
    },

    onBuilderCancel() {
      this.destroyBuilderMiniMap();
      this.showBuilderDialog = false;
      this.showDuplicateMarkerDialog = false;
      this.duplicateMarkerSource = null;
      this.duplicateMarkerCsvContent = '';
      this.duplicateMarkerNameChanges = [];
      this.showCloseMarkerWarningDialog = false;
      this.closeMarkerSource = null;
      this.closeMarkerCsvContent = '';
      this.closeMarkerPairs = [];
      this.builderOperation = 'create';
    },

    onBuilderBack() {
      if (this.builderStep === 5) {
        this.destroyBuilderMiniMap();
      }
      this.builderStep--;
    },

    onBuilderNext() {
      if (this.builderStep === 1) {
        const changes = this.getDuplicateMarkerNameChanges(
          this.builderMarkerNames
        );
        if (changes.length > 0) {
          this.duplicateMarkerNameChanges = changes;
          this.duplicateMarkerSource = 'builder';
          this.duplicateMarkerCsvContent = '';
          this.showDuplicateMarkerDialog = true;
          return;
        }
        this.ensureBuilderTrackStates();
        this.builderStep = 2;
        this.validateBuilderCoords();
        return;
      }
      if (this.builderStep === 2) {
        this.validateBuilderCoords();
        if (this.builderCoordErrors.length > 0) {
          return;
        }

        const closePairs = this.getCloseMarkerPairs();
        if (closePairs.length > 0) {
          this.closeMarkerPairs = closePairs;
          this.closeMarkerSource = 'builder';
          this.closeMarkerCsvContent = '';
          this.showCloseMarkerWarningDialog = true;
          return;
        }

        this.builderStep = 3;
        return;
      }
      if (this.builderStep === 3) {
        this.builderStep = 4;
        this.validateBuilderDistances();
        return;
      }
      if (this.builderStep === 4) {
        this.builderStep = 5;
        this.$nextTick(() => {
          this.plotBuilderCoordinatesOnMiniMap();
        });
        return;
      }
      if (this.builderStep === 5) {
        const trackingFile = this.generateBuilderTrackingFile();
        const validation = this.validateCsv(trackingFile);
        if (!validation.valid) {
          console.error('Generated builder CSV failed validation:', validation);
          this.$q.notify({
            color: 'negative',
            message: 'Generated builder CSV is invalid. Check the console.',
            timeout: 3000,
          });
          return;
        }

        if (this.builderOperation === 'update') {
          if (!this.selectedPipelineSection || !this.validateUpdateDiameter()) {
            return;
          }

          const updateRequest = new UpdatePipelineRequest(
            this.selectedPipelineSection.value,
            this.getUpdateDiameterMeters(),
            'pipeline-builder.csv',
            trackingFile
          );
          console.log('UpdatePipelineRequest (builder path):', updateRequest);
          this.updateAction = 'update';
          this.loading = true;
          PipelineSectionService.updatePipelineSection(updateRequest)
            .then(() => {
              this.$q.notify({
                color: 'positive',
                message: 'Pipeline updated.',
              });
              this.destroyBuilderMiniMap();
              this.showBuilderDialog = false;
              this.$emit('update:modelValue', false);
              this.refreshAndEmit();
            })
            .catch((error: any) => {
              console.error('Failed to update pipeline:', error);
              this.$q.notify({
                color: 'negative',
                message: 'Failed to update pipeline.',
              });
            })
            .finally(() => {
              this.updateAction = null;
              this.loading = false;
            });
          return;
        }

        const request = this.createPipelineRequest(
          'pipeline-builder.csv',
          trackingFile
        );
        console.log('CreatePipelineRequest (builder path):', request);
        this.loading = true;
        PipelineSectionService.createPipelineSection(request)
          .then(() => {
            this.$q.notify({
              color: 'positive',
              message: 'Pipeline created.',
            });
            this.destroyBuilderMiniMap();
            this.showBuilderDialog = false;
            this.$emit('update:modelValue', false);
            this.refreshAndEmit();
          })
          .catch((error: any) => {
            console.error('Failed to create pipeline:', error);
            this.$q.notify({
              color: 'negative',
              message: 'Failed to create pipeline.',
            });
          })
          .finally(() => {
            this.loading = false;
          });
      }
    },

    getDuplicateMarkerNameChanges(names: string[]): {
      row: number;
      originalName: string;
      proposedName: string;
    }[] {
      const reservedNames = new Set(
        names.map((name) => name.toLocaleLowerCase())
      );
      const occurrenceCounts = new Map<string, number>();
      for (const name of names) {
        const nameKey = name.toLocaleLowerCase();
        occurrenceCounts.set(nameKey, (occurrenceCounts.get(nameKey) || 0) + 1);
      }
      const usedNames = new Set<string>();
      const nextSuffixes = new Map<string, number>();
      const changes: {
        row: number;
        originalName: string;
        proposedName: string;
      }[] = [];

      for (let index = 0; index < names.length; index++) {
        const originalName = names[index];
        const originalKey = originalName.toLocaleLowerCase();

        if ((occurrenceCounts.get(originalKey) || 0) === 1) {
          usedNames.add(originalKey);
          continue;
        }

        let suffix = nextSuffixes.get(originalKey) || 1;
        let proposedName = `${originalName}_${suffix}`;
        while (
          reservedNames.has(proposedName.toLocaleLowerCase()) ||
          usedNames.has(proposedName.toLocaleLowerCase())
        ) {
          suffix++;
          proposedName = `${originalName}_${suffix}`;
        }

        nextSuffixes.set(originalKey, suffix + 1);
        usedNames.add(proposedName.toLocaleLowerCase());
        changes.push({ row: index + 1, originalName, proposedName });
      }

      return changes;
    },

    initializeBuilderTrackStates() {
      this.builderTrackStateMarkerNames = [...this.builderMarkerNames];
      this.builderTrackStates = this.builderMarkerNames.map(
        (_markerName, index) =>
          index === 0 || index === this.builderMarkerNames.length - 1
            ? 'S'
            : 'T'
      );
    },

    ensureBuilderTrackStates() {
      const markerNamesMatch =
        this.builderTrackStateMarkerNames.length ===
          this.builderMarkerNames.length &&
        this.builderTrackStateMarkerNames.every(
          (markerName, index) => markerName === this.builderMarkerNames[index]
        );

      if (!markerNamesMatch || !this.builderTrackStatesValid) {
        this.initializeBuilderTrackStates();
      }
    },

    onRejectDuplicateMarkerNames() {
      const source = this.duplicateMarkerSource;
      const content = this.duplicateMarkerCsvContent;
      this.showDuplicateMarkerDialog = false;
      this.duplicateMarkerSource = null;
      this.duplicateMarkerCsvContent = '';
      this.duplicateMarkerNameChanges = [];

      if ((source === 'create' || source === 'update') && content) {
        this.maybeShowCsvCloseMarkerWarning(source, content);
      }
    },

    onAcceptDuplicateMarkerNames() {
      if (
        this.duplicateMarkerSource === 'create' ||
        this.duplicateMarkerSource === 'update'
      ) {
        this.onAcceptCsvDuplicateMarkerNames();
        return;
      }

      const proposedNamesByRow = new Map(
        this.duplicateMarkerNameChanges.map((change) => [
          change.row,
          change.proposedName,
        ])
      );
      this.builderMarkerNamesRaw = this.builderMarkerNames
        .map((name, index) => proposedNamesByRow.get(index + 1) || name)
        .join('\n');
      this.validateBuilderMarkerNames();
      this.showDuplicateMarkerDialog = false;
      this.duplicateMarkerSource = null;
      this.duplicateMarkerCsvContent = '';
      this.duplicateMarkerNameChanges = [];
      this.initializeBuilderTrackStates();
      this.builderStep = 2;
      this.validateBuilderCoords();
    },

    onAcceptCsvDuplicateMarkerNames() {
      const source = this.duplicateMarkerSource;
      const content = this.duplicateMarkerCsvContent;
      const changes = [...this.duplicateMarkerNameChanges];
      this.showDuplicateMarkerDialog = false;
      this.duplicateMarkerSource = null;
      this.duplicateMarkerCsvContent = '';
      this.duplicateMarkerNameChanges = [];

      if (
        (source !== 'create' && source !== 'update') ||
        !content ||
        changes.length === 0
      ) {
        return;
      }

      let csvContent = content;
      if (csvContent.charCodeAt(0) === 0xfeff) {
        csvContent = csvContent.slice(1);
      }

      const lines = csvContent
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line.length > 0);
      if (lines.length < 2) return;

      const header = lines[0].split(',').map((h) => h.trim().toLowerCase());
      const nameIdx = header.indexOf('agm marker name');
      if (nameIdx === -1) return;

      const proposedNamesByRow = new Map(
        changes.map((change) => [change.row, change.proposedName])
      );

      for (let i = 1; i < lines.length; i++) {
        const lineNumber = i + 1;
        const proposedName = proposedNamesByRow.get(lineNumber);
        if (!proposedName) continue;

        const cols = lines[i].split(',').map((c) => c.trim());
        if (cols.length <= nameIdx) continue;
        cols[nameIdx] = proposedName;
        lines[i] = cols.join(',');
      }

      const newContent = lines.join('\n') + '\n';
      const originalFile =
        source === 'create' ? this.createCsvFile : this.updateCsvFile;
      const fileName = originalFile?.name || 'pipeline.csv';
      const rewrittenFile = new File([newContent], fileName, {
        type: 'text/csv',
      });

      if (source === 'create') {
        this.createCsvFile = rewrittenFile;
      } else {
        this.updateCsvFile = rewrittenFile;
      }
    },

    getHaversineDistanceMeters(
      firstLatitude: number,
      firstLongitude: number,
      secondLatitude: number,
      secondLongitude: number
    ): number {
      const toRadians = (degrees: number) => (degrees * Math.PI) / 180;
      const latitudeDelta = toRadians(secondLatitude - firstLatitude);
      const longitudeDelta = toRadians(secondLongitude - firstLongitude);
      const haversine =
        Math.sin(latitudeDelta / 2) ** 2 +
        Math.cos(toRadians(firstLatitude)) *
          Math.cos(toRadians(secondLatitude)) *
          Math.sin(longitudeDelta / 2) ** 2;
      return (
        6371000 * 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine))
      );
    },

    getCloseMarkerPairsFromPoints(
      points: {
        latitude: number;
        longitude: number;
        name: string;
        row: number;
      }[]
    ): {
      earlierRow: number;
      laterRow: number;
      earlierName: string;
      laterName: string;
      meters: number;
    }[] {
      const closePairs: {
        earlierRow: number;
        laterRow: number;
        earlierName: string;
        laterName: string;
        meters: number;
      }[] = [];

      for (let index = 1; index < points.length; index++) {
        const earlier = points[index - 1];
        const later = points[index];
        const meters = this.getHaversineDistanceMeters(
          earlier.latitude,
          earlier.longitude,
          later.latitude,
          later.longitude
        );
        if (meters < 2) {
          closePairs.push({
            earlierRow: earlier.row,
            laterRow: later.row,
            earlierName: earlier.name,
            laterName: later.name,
            meters,
          });
        }
      }

      return closePairs;
    },

    getCloseMarkerPairs(): {
      earlierRow: number;
      laterRow: number;
      earlierName: string;
      laterName: string;
      meters: number;
    }[] {
      const latitudes = this.getBuilderColumnValues(this.builderLatitudesRaw);
      const longitudes = this.getBuilderColumnValues(this.builderLongitudesRaw);

      return this.getCloseMarkerPairsFromPoints(
        this.builderMarkerNames.map((name, index) => ({
          latitude: Number(latitudes[index]),
          longitude: Number(longitudes[index]),
          name,
          row: index + 1,
        }))
      );
    },

    maybeShowCsvDuplicateOrCloseWarning(
      source: 'create' | 'update',
      content: string
    ) {
      const validation =
        source === 'create'
          ? this.createCsvValidation
          : this.updateCsvValidation;
      if (!validation) return;

      if (validation.duplicateMarkerNameChanges.length > 0) {
        this.duplicateMarkerNameChanges = [
          ...validation.duplicateMarkerNameChanges,
        ];
        this.duplicateMarkerSource = source;
        this.duplicateMarkerCsvContent = content;
        this.showDuplicateMarkerDialog = true;
        return;
      }

      this.maybeShowCsvCloseMarkerWarning(source, content);
    },

    maybeShowCsvCloseMarkerWarning(
      source: 'create' | 'update',
      content: string
    ) {
      const validation =
        source === 'create'
          ? this.createCsvValidation
          : this.updateCsvValidation;
      if (!validation || validation.closeMarkerPairs.length === 0) {
        return;
      }

      this.closeMarkerPairs = [...validation.closeMarkerPairs];
      this.closeMarkerSource = source;
      this.closeMarkerCsvContent = content;
      this.showCloseMarkerWarningDialog = true;
    },

    onRejectCloseMarkerWarning() {
      this.showCloseMarkerWarningDialog = false;
      this.closeMarkerSource = null;
      this.closeMarkerCsvContent = '';
      this.closeMarkerPairs = [];
    },

    onRemoveCloseMarkerRows() {
      if (
        this.closeMarkerSource === 'create' ||
        this.closeMarkerSource === 'update'
      ) {
        this.onRemoveCsvCloseMarkerRows();
        return;
      }

      const rowsToRemove = new Set(
        this.closeMarkerPairs.map((pair) => pair.laterRow - 1)
      );
      const removeRows = <T>(values: T[]): T[] =>
        values.filter((_value, index) => !rowsToRemove.has(index));

      this.builderMarkerNamesRaw = removeRows(this.builderMarkerNames).join(
        '\n'
      );
      this.builderLatitudesRaw = removeRows(
        this.getBuilderColumnValues(this.builderLatitudesRaw)
      ).join('\n');
      this.builderLongitudesRaw = removeRows(
        this.getBuilderColumnValues(this.builderLongitudesRaw)
      ).join('\n');
      if (this.builderDistancesRaw) {
        this.builderDistancesRaw = removeRows(
          this.getBuilderColumnValues(this.builderDistancesRaw)
        ).join('\n');
      }

      this.showCloseMarkerWarningDialog = false;
      this.closeMarkerSource = null;
      this.closeMarkerCsvContent = '';
      this.closeMarkerPairs = [];
      this.validateBuilderMarkerNames();
      this.validateBuilderCoords();
      this.initializeBuilderTrackStates();
      if (this.builderMarkerNameErrors.length > 0) {
        this.builderStep = 1;
        return;
      }
      if (this.builderDistancesRaw) {
        this.validateBuilderDistances();
      }
    },

    onRemoveCsvCloseMarkerRows() {
      const source = this.closeMarkerSource;
      const content = this.closeMarkerCsvContent;
      const pairs = [...this.closeMarkerPairs];
      this.showCloseMarkerWarningDialog = false;
      this.closeMarkerSource = null;
      this.closeMarkerCsvContent = '';
      this.closeMarkerPairs = [];

      if (
        (source !== 'create' && source !== 'update') ||
        !content ||
        pairs.length === 0
      ) {
        return;
      }

      let csvContent = content;
      if (csvContent.charCodeAt(0) === 0xfeff) {
        csvContent = csvContent.slice(1);
      }

      // Match validateCsv line numbering: trimmed, non-empty lines
      const lines = csvContent
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line.length > 0);
      const linesToRemove = new Set(pairs.map((pair) => pair.laterRow));
      const keptLines = lines.filter(
        (_line, index) => !linesToRemove.has(index + 1)
      );
      const newContent =
        keptLines.join('\n') + (keptLines.length > 0 ? '\n' : '');

      const originalFile =
        source === 'create' ? this.createCsvFile : this.updateCsvFile;
      const fileName = originalFile?.name || 'pipeline.csv';
      const rewrittenFile = new File([newContent], fileName, {
        type: 'text/csv',
      });

      if (source === 'create') {
        this.createCsvFile = rewrittenFile;
      } else {
        this.updateCsvFile = rewrittenFile;
      }
    },

    validateBuilderCoords() {
      const errors: string[] = [];
      const expectedCount = this.builderMarkerNames.length;

      const lats = this.builderLatitudesRaw
        .split('\n')
        .map((l: string) => l.trim())
        .filter((l: string) => l.length > 0);
      const lons = this.builderLongitudesRaw
        .split('\n')
        .map((l: string) => l.trim())
        .filter((l: string) => l.length > 0);

      if (lats.length === 0) {
        errors.push(`Exactly ${expectedCount} latitudes are required.`);
      } else if (lats.length !== expectedCount) {
        errors.push(
          `Expected ${expectedCount} latitudes but found ${lats.length}.`
        );
      }
      if (lons.length === 0) {
        errors.push(`Exactly ${expectedCount} longitudes are required.`);
      } else if (lons.length !== expectedCount) {
        errors.push(
          `Expected ${expectedCount} longitudes but found ${lons.length}.`
        );
      }

      for (let i = 0; i < lats.length; i++) {
        const v = Number(lats[i]);
        if (isNaN(v)) {
          errors.push(`Latitude on line ${i + 1} is not a valid number.`);
        } else if (v < -90 || v > 90) {
          errors.push(`Latitude on line ${i + 1} is out of range (-90 to 90).`);
        }
      }

      for (let i = 0; i < lons.length; i++) {
        const v = Number(lons[i]);
        if (isNaN(v)) {
          errors.push(`Longitude on line ${i + 1} is not a valid number.`);
        } else if (v < -180 || v > 180) {
          errors.push(
            `Longitude on line ${i + 1} is out of range (-180 to 180).`
          );
        }
      }

      const coordinateRows = new Map<string, number[]>();
      const coordinateCount = Math.min(lats.length, lons.length);
      for (let i = 0; i < coordinateCount; i++) {
        const latitude = Number(lats[i]);
        const longitude = Number(lons[i]);
        if (
          isNaN(latitude) ||
          isNaN(longitude) ||
          latitude < -90 ||
          latitude > 90 ||
          longitude < -180 ||
          longitude > 180
        ) {
          continue;
        }

        const coordinateKey = `${latitude},${longitude}`;
        if (!coordinateRows.has(coordinateKey)) {
          coordinateRows.set(coordinateKey, []);
        }
        coordinateRows.get(coordinateKey)!.push(i + 1);
      }

      for (const [coordinate, rows] of coordinateRows) {
        if (rows.length > 1) {
          errors.push(
            `Duplicate coordinate (${coordinate}) on rows ${rows.join(', ')}.`
          );
        }
      }

      this.builderCoordErrors = errors;
    },

    onBuilderDistanceInput() {
      this.builderDistanceSkipped = false;
      this.validateBuilderDistances();
    },

    onBuilderDistanceUnitChange() {
      this.builderDistanceSkipped = false;
      this.validateBuilderDistances();
    },

    onSkipBuilderDistances() {
      this.builderDistancesRaw = '';
      this.builderDistanceErrors = [];
      this.builderDistanceSkipped = true;
    },

    validateBuilderDistances() {
      if (this.builderDistanceSkipped) {
        this.builderDistanceErrors = [];
        return;
      }

      const errors: string[] = [];
      const expectedCount = this.builderMarkerNames.length;
      const distances = this.builderDistancesRaw
        .split('\n')
        .map((value: string) => value.trim())
        .filter((value: string) => value.length > 0);

      if (distances.length === 0) {
        errors.push(
          `Paste ${expectedCount} cumulative ${this.builderDistanceUnit} values or skip this step.`
        );
      } else if (distances.length !== expectedCount) {
        errors.push(
          `Expected ${expectedCount} ${this.builderDistanceUnit} values but found ${distances.length}.`
        );
      }

      let previousDistance: number | null = null;
      for (let index = 0; index < distances.length; index++) {
        const distance = Number(distances[index].replace(/,/g, ''));
        if (!Number.isFinite(distance)) {
          errors.push(`Distance on line ${index + 1} is not a valid number.`);
          continue;
        }
        if (distance < 0) {
          errors.push(`Distance on line ${index + 1} cannot be negative.`);
          continue;
        }
        if (index === 0 && distance !== 0) {
          errors.push(`First distance must be 0, found ${distance} on line 1.`);
        }
        if (previousDistance !== null && distance <= previousDistance) {
          errors.push(
            `Pipeline distance must increase: ${previousDistance} to ${distance} on line ${
              index + 1
            }.`
          );
        }
        previousDistance = distance;
      }

      this.builderDistanceErrors = errors;
    },

    validateBuilderMarkerNames() {
      const errors: string[] = [];
      const raw = this.builderMarkerNamesRaw;
      const names = raw
        .split('\n')
        .map((n: string) => n.trim())
        .filter((n: string) => n.length > 0);

      this.builderMarkerNames = names;
      this.showDuplicateMarkerDialog = false;
      this.duplicateMarkerSource = null;
      this.duplicateMarkerCsvContent = '';
      this.duplicateMarkerNameChanges = [];

      if (names.length === 0) {
        this.builderMarkerNameErrors = [];
        return;
      }

      if (names.length < 2) {
        errors.push(
          'At least 2 marker names are required (Launch and Receive).'
        );
      }

      for (let i = 0; i < names.length; i++) {
        if (names[i].length > 100) {
          errors.push(`Name on line ${i + 1} exceeds 100 characters.`);
        }
      }

      this.builderMarkerNameErrors = errors;
    },

    onDropCsv(event: DragEvent, target: 'create' | 'update') {
      this.createDragOver = false;
      this.updateDragOver = false;
      if (target === 'update' && !this.selectedPipelineSection) return;
      const file = event.dataTransfer?.files?.[0];
      if (!file) return;
      if (!file.name.toLowerCase().endsWith('.csv')) {
        this.$q.notify({
          color: 'warning',
          message: 'Only .csv files are accepted.',
          timeout: 2000,
        });
        return;
      }
      if (target === 'create') {
        this.createCsvFile = file;
      } else {
        this.updateCsvFile = file;
      }
    },

    hasRowRef(errMsg: string): boolean {
      return /rows?\s+\d+/i.test(errMsg);
    },

    scrollToErrorRow(tableRef: string, errMsg: string) {
      const match = errMsg.match(/rows?\s+(\d+)/i);
      if (!match) return;
      const rowNum = match[1];
      const table = (this.$refs[tableRef] as any)?.$el;
      if (!table) return;
      const tr = table.querySelector(`tr[data-row="${rowNum}"]`);
      if (tr) {
        tr.scrollIntoView({ block: 'center', behavior: 'smooth' });
        tr.classList.add('highlight-flash');
        setTimeout(() => tr.classList.remove('highlight-flash'), 1500);
      }
    },

    validateCsv(content: string): {
      valid: boolean;
      errors: string[];
      warnings: string[];
      markerCount: number;
      firstMarker: string;
      lastMarker: string;
      skipCount: number;
      hasDistance: boolean;
      distanceUnit: 'meters' | 'feet' | 'miles' | null;
      closeMarkerPairs: {
        earlierRow: number;
        laterRow: number;
        earlierName: string;
        laterName: string;
        meters: number;
      }[];
      duplicateMarkerNameChanges: {
        row: number;
        originalName: string;
        proposedName: string;
      }[];
      parsedRows: {
        row: number;
        name: string;
        lat: string;
        lon: string;
        distance: string;
        state: string;
        description: string;
        hasError: boolean;
        errorFields: string[];
      }[];
    } {
      const errors: string[] = [];
      const warnings: string[] = [];
      let markerCount = 0;
      let firstMarker = '';
      let lastMarker = '';
      let skipCount = 0;
      const closeMarkerPairs: {
        earlierRow: number;
        laterRow: number;
        earlierName: string;
        laterName: string;
        meters: number;
      }[] = [];
      const duplicateMarkerNameChanges: {
        row: number;
        originalName: string;
        proposedName: string;
      }[] = [];
      const parsedRows: {
        row: number;
        name: string;
        lat: string;
        lon: string;
        distance: string;
        state: string;
        description: string;
        hasError: boolean;
        errorFields: string[];
      }[] = [];

      // Strip UTF-8 BOM if present
      if (content.charCodeAt(0) === 0xfeff) {
        content = content.slice(1);
        warnings.push(
          'File contains a UTF-8 BOM (byte order mark) which was automatically removed.'
        );
      }

      const lines = content
        .split('\n')
        .map((l) => l.trim())
        .filter((l) => l.length > 0);

      if (lines.length === 0) {
        errors.push('File is empty.');
        return {
          valid: false,
          errors,
          warnings,
          markerCount,
          firstMarker,
          lastMarker,
          skipCount,
          hasDistance: false,
          distanceUnit: null,
          closeMarkerPairs,
          duplicateMarkerNameChanges,
          parsedRows,
        };
      }

      const header = lines[0].split(',').map((h) => h.trim().toLowerCase());
      const latIdx = header.indexOf('latitude');
      const lonIdx = header.indexOf('longitude');
      const nameIdx = header.indexOf('agm marker name');
      let distIdx = header.indexOf('pipeline distance (meters)');
      let distanceUnit: 'meters' | 'feet' | 'miles' | null =
        distIdx !== -1 ? 'meters' : null;
      if (distIdx === -1) {
        distIdx = header.indexOf('pipeline distance (feet)');
        if (distIdx !== -1) distanceUnit = 'feet';
      }
      if (distIdx === -1) {
        distIdx = header.indexOf('pipeline distance (miles)');
        if (distIdx !== -1) distanceUnit = 'miles';
      }
      const hasDistance = distIdx !== -1;
      const expectedColCount = hasDistance ? 6 : 5;
      const stateIdx = header.indexOf('state');
      const descIdx = header.indexOf('description');

      if (latIdx === -1) errors.push('Missing required column: Latitude');
      if (lonIdx === -1) errors.push('Missing required column: Longitude');
      if (nameIdx === -1)
        errors.push('Missing required column: AGM Marker Name');
      if (stateIdx === -1) errors.push('Missing required column: State');
      if (descIdx === -1) errors.push('Missing required column: Description');

      if (
        latIdx === -1 ||
        lonIdx === -1 ||
        nameIdx === -1 ||
        stateIdx === -1 ||
        descIdx === -1
      ) {
        return {
          valid: false,
          errors,
          warnings,
          markerCount,
          firstMarker,
          lastMarker,
          skipCount,
          hasDistance,
          distanceUnit,
          closeMarkerPairs,
          duplicateMarkerNameChanges,
          parsedRows,
        };
      }

      if (lines.length < 3) {
        errors.push('At least 2 marker rows are required.');
        return {
          valid: false,
          errors,
          warnings,
          markerCount,
          firstMarker,
          lastMarker,
          skipCount,
          hasDistance,
          distanceUnit,
          closeMarkerPairs,
          duplicateMarkerNameChanges,
          parsedRows,
        };
      }

      const nonNumericCoordRows: { row: number; lat: string; lon: string }[] =
        [];
      const badDistRows: { row: number; value: string }[] = [];
      const badLatRows: { row: number; value: string }[] = [];
      const badLonRows: { row: number; value: string }[] = [];
      const badColCountRows: number[] = [];
      const badStateRows: { row: number; value: string }[] = [];
      const badDescRows: { row: number; value: string }[] = [];
      const emptyNameRows: number[] = [];
      const longNameRows: number[] = [];
      let prevDistance = -1;
      const markers: string[] = [];
      const descriptions: { row: number; value: string }[] = [];
      const nameOccurrences: Record<
        string,
        { displayName: string; rows: number[] }
      > = {};
      const namesForDuplicateFix: { name: string; row: number }[] = [];
      const coordOccurrences: Record<string, number[]> = {};
      const coordPoints: {
        latitude: number;
        longitude: number;
        name: string;
        row: number;
      }[] = [];
      const validDescriptions = new Set([
        'AGM',
        'ATTACHMENT',
        'BEND',
        'LAUNCH',
        'MAGNET',
        'OTHER',
        'PIPE',
        'RECEIVE',
        'TAP',
        'TEE',
        'VALVE',
        'WELD',
      ]);

      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(',').map((c) => c.trim());

        if (cols.length !== expectedColCount) {
          badColCountRows.push(i + 1);
          const allFields = ['name', 'lat', 'lon', 'state', 'description'];
          if (hasDistance) allFields.splice(3, 0, 'distance');
          parsedRows.push({
            row: i + 1,
            name: cols[nameIdx] || '',
            lat: cols[latIdx] || '',
            lon: cols[lonIdx] || '',
            distance: hasDistance ? cols[distIdx] || '' : '',
            state: cols[stateIdx] || '',
            description: cols[descIdx] || '',
            hasError: true,
            errorFields: allFields,
          });
          continue;
        }

        const lat = Number(cols[latIdx]);
        const lon = Number(cols[lonIdx]);

        if (isNaN(lat) || isNaN(lon)) {
          nonNumericCoordRows.push({
            row: i + 1,
            lat: cols[latIdx],
            lon: cols[lonIdx],
          });
          const coordErrors: string[] = [];
          if (isNaN(lat)) coordErrors.push('lat');
          if (isNaN(lon)) coordErrors.push('lon');
          parsedRows.push({
            row: i + 1,
            name: cols[nameIdx] || '',
            lat: cols[latIdx],
            lon: cols[lonIdx],
            distance: hasDistance ? cols[distIdx] || '' : '',
            state: cols[stateIdx] || '',
            description: cols[descIdx] || '',
            hasError: true,
            errorFields: coordErrors,
          });
          continue;
        }
        if (lat < -90 || lat > 90) {
          badLatRows.push({ row: i + 1, value: cols[latIdx] });
          parsedRows.push({
            row: i + 1,
            name: cols[nameIdx] || '',
            lat: cols[latIdx],
            lon: cols[lonIdx],
            distance: hasDistance ? cols[distIdx] || '' : '',
            state: cols[stateIdx] || '',
            description: cols[descIdx] || '',
            hasError: true,
            errorFields: ['lat'],
          });
          continue;
        }
        if (lon < -180 || lon > 180) {
          badLonRows.push({ row: i + 1, value: cols[lonIdx] });
          parsedRows.push({
            row: i + 1,
            name: cols[nameIdx] || '',
            lat: cols[latIdx],
            lon: cols[lonIdx],
            distance: hasDistance ? cols[distIdx] || '' : '',
            state: cols[stateIdx] || '',
            description: cols[descIdx] || '',
            hasError: true,
            errorFields: ['lon'],
          });
          continue;
        }

        markerCount++;
        const rawName = nameIdx !== -1 ? cols[nameIdx] || '' : '';
        if (!rawName) {
          emptyNameRows.push(i + 1);
        } else if (rawName.length > 100) {
          longNameRows.push(i + 1);
        }
        const name = rawName || `Row ${i + 1}`;
        markers.push(name);
        coordPoints.push({
          latitude: lat,
          longitude: lon,
          name,
          row: i + 1,
        });

        // Track name occurrences for case-insensitive duplicate check
        if (rawName) {
          namesForDuplicateFix.push({ name: rawName, row: i + 1 });
          const nameKey = rawName.toLocaleLowerCase();
          if (!nameOccurrences[nameKey]) {
            nameOccurrences[nameKey] = { displayName: rawName, rows: [] };
          }
          nameOccurrences[nameKey].rows.push(i + 1);
        }

        // Track coordinate occurrences for duplicate check
        const coordKey = `${lat},${lon}`;
        if (!coordOccurrences[coordKey]) {
          coordOccurrences[coordKey] = [];
        }
        coordOccurrences[coordKey].push(i + 1);

        // Validate pipeline distance when present
        let distValue = '';
        if (hasDistance) {
          distValue = cols[distIdx] || '';
          const dist = Number(distValue);
          if (distValue === '' || isNaN(dist)) {
            badDistRows.push({ row: i + 1, value: distValue });
          } else if (dist < 0) {
            badDistRows.push({ row: i + 1, value: distValue });
          } else {
            if (prevDistance === -1 && dist !== 0) {
              errors.push(
                `First data row (Launch) Pipeline Distance must be 0, found ${dist} on row ${
                  i + 1
                }.`
              );
            }
            if (prevDistance >= 0 && dist <= prevDistance) {
              errors.push(
                `Pipeline Distance must increase monotonically: ${prevDistance} to ${dist} on row ${
                  i + 1
                }.`
              );
            }
            prevDistance = dist;
          }
        }

        // Track description
        const desc = descIdx !== -1 ? (cols[descIdx] || '').toUpperCase() : '';
        descriptions.push({ row: i + 1, value: desc });
        if (desc && !validDescriptions.has(desc)) {
          badDescRows.push({ row: i + 1, value: cols[descIdx] });
        }

        if (stateIdx !== -1) {
          const state = (cols[stateIdx] || '').toUpperCase();
          if (state === 'SKIP') skipCount++;
          if (state !== 'TRACK' && state !== 'SKIP') {
            badStateRows.push({ row: i + 1, value: cols[stateIdx] });
          }
        }

        const errorFields: string[] = [];
        if (!rawName) errorFields.push('name');
        else if (rawName.length > 100) errorFields.push('name');
        if (hasDistance) {
          const dist = Number(distValue);
          if (distValue === '' || isNaN(dist) || dist < 0)
            errorFields.push('distance');
        }
        if (desc !== '' && !validDescriptions.has(desc))
          errorFields.push('description');
        if (
          stateIdx !== -1 &&
          (cols[stateIdx] || '').toUpperCase() !== 'TRACK' &&
          (cols[stateIdx] || '').toUpperCase() !== 'SKIP'
        )
          errorFields.push('state');
        const rowHasError = errorFields.length > 0;
        parsedRows.push({
          row: i + 1,
          name: rawName || '',
          lat: cols[latIdx],
          lon: cols[lonIdx],
          distance: distValue,
          state: cols[stateIdx] || '',
          description: cols[descIdx] || '',
          hasError: rowHasError,
          errorFields,
        });
      }

      if (badColCountRows.length > 0) {
        const shown = badColCountRows.slice(0, 5);
        const msg = `Row${badColCountRows.length > 1 ? 's' : ''} ${shown.join(
          ', '
        )} must have exactly ${expectedColCount} columns${
          badColCountRows.length > 5
            ? ` and ${badColCountRows.length - 5} more`
            : ''
        }`;
        errors.push(msg);
      }

      if (nonNumericCoordRows.length > 0) {
        const shown = nonNumericCoordRows.slice(0, 5);
        const msgs = shown.map(
          (r) => `row ${r.row} (Latitude: "${r.lat}", Longitude: "${r.lon}")`
        );
        const extra =
          nonNumericCoordRows.length > 5
            ? ` and ${nonNumericCoordRows.length - 5} more`
            : '';
        errors.push(`Non-numeric coordinates on ${msgs.join('; ')}${extra}`);
      }

      if (badDistRows.length > 0) {
        const shown = badDistRows.slice(0, 5);
        const msgs = shown.map((r) => `"${r.value}" on row ${r.row}`);
        const extra =
          badDistRows.length > 5 ? ` and ${badDistRows.length - 5} more` : '';
        errors.push(
          `Invalid Pipeline Distance: ${msgs.join(
            '; '
          )}${extra} (must be a non-negative number)`
        );
      }

      if (badLatRows.length > 0) {
        const shown = badLatRows.slice(0, 5);
        const msgs = shown.map((r) => `"${r.value}" on row ${r.row}`);
        const extra =
          badLatRows.length > 5 ? ` and ${badLatRows.length - 5} more` : '';
        errors.push(
          `Invalid latitude: ${msgs.join(
            '; '
          )}${extra} (must be between -90 and 90)`
        );
      }

      if (badLonRows.length > 0) {
        const shown = badLonRows.slice(0, 5);
        const msgs = shown.map((r) => `"${r.value}" on row ${r.row}`);
        const extra =
          badLonRows.length > 5 ? ` and ${badLonRows.length - 5} more` : '';
        errors.push(
          `Invalid longitude: ${msgs.join(
            '; '
          )}${extra} (must be between -180 and 180)`
        );
      }

      if (badStateRows.length > 0) {
        const shown = badStateRows.slice(0, 5);
        const msgs = shown.map((r) => `"${r.value}" on row ${r.row}`);
        const extra =
          badStateRows.length > 5 ? ` and ${badStateRows.length - 5} more` : '';
        errors.push(
          `Invalid State value: ${msgs.join(
            '; '
          )}${extra} (must be "TRACK" or "SKIP")`
        );
      }

      if (emptyNameRows.length > 0) {
        const shown = emptyNameRows.slice(0, 5);
        const msg = `AGM Marker Name is empty on row${
          emptyNameRows.length > 1 ? 's' : ''
        } ${shown.join(', ')}${
          emptyNameRows.length > 5
            ? ` and ${emptyNameRows.length - 5} more`
            : ''
        }`;
        errors.push(msg);
      }

      if (longNameRows.length > 0) {
        const shown = longNameRows.slice(0, 5);
        const msg = `AGM Marker Name exceeds 100 characters on row${
          longNameRows.length > 1 ? 's' : ''
        } ${shown.join(', ')}${
          longNameRows.length > 5 ? ` and ${longNameRows.length - 5} more` : ''
        }`;
        errors.push(msg);
      }

      if (markerCount < 2) {
        errors.push(
          `Only ${markerCount} valid marker(s) found; at least 2 are required.`
        );
      }

      // Check for duplicate AGM Marker Names (case-insensitive)
      const duplicates = Object.values(nameOccurrences).filter(
        (entry) => entry.rows.length > 1
      );
      if (duplicates.length > 0) {
        const shown = duplicates.slice(0, 3);
        const msgs = shown.map(
          (entry) => `"${entry.displayName}" (rows ${entry.rows.join(', ')})`
        );
        const extra =
          duplicates.length > 3 ? ` and ${duplicates.length - 3} more` : '';
        errors.push(`Duplicate AGM Marker Names: ${msgs.join('; ')}${extra}`);

        const indexChanges = this.getDuplicateMarkerNameChanges(
          namesForDuplicateFix.map((entry) => entry.name)
        );
        for (const change of indexChanges) {
          const fileRow = namesForDuplicateFix[change.row - 1]?.row;
          if (fileRow == null) continue;
          duplicateMarkerNameChanges.push({
            row: fileRow,
            originalName: change.originalName,
            proposedName: change.proposedName,
          });
        }
      }

      // Check for duplicate coordinates
      const dupCoords = Object.entries(coordOccurrences).filter(
        ([, rows]) => rows.length > 1
      );
      if (dupCoords.length > 0) {
        const shown = dupCoords.slice(0, 3);
        const msgs = shown.map(
          ([coord, rows]) => `(${coord}) on rows ${rows.join(', ')}`
        );
        const extra =
          dupCoords.length > 3 ? ` and ${dupCoords.length - 3} more` : '';
        errors.push(`Duplicate coordinates: ${msgs.join('; ')}${extra}`);
      }

      // Check consecutive markers closer than 2 m (Haversine)
      const detectedClosePairs =
        this.getCloseMarkerPairsFromPoints(coordPoints);
      if (detectedClosePairs.length > 0) {
        closeMarkerPairs.push(...detectedClosePairs);
        const shown = detectedClosePairs.slice(0, 3);
        const msgs = shown.map(
          (pair) =>
            `row ${pair.laterRow} "${pair.laterName}" is ${pair.meters.toFixed(
              1
            )} m from row ${pair.earlierRow} "${pair.earlierName}"`
        );
        const extra =
          detectedClosePairs.length > 3
            ? ` and ${detectedClosePairs.length - 3} more`
            : '';
        errors.push(`Markers closer than 2 m: ${msgs.join('; ')}${extra}`);
      }

      // Back-patch errorFields for duplicate rows
      if (duplicates.length > 0) {
        const dupNameRows = new Set(duplicates.flatMap((entry) => entry.rows));
        for (const pr of parsedRows) {
          if (dupNameRows.has(pr.row) && !pr.errorFields.includes('name')) {
            pr.errorFields.push('name');
            pr.hasError = true;
          }
        }
      }
      if (dupCoords.length > 0) {
        const dupCoordRows = new Set(dupCoords.flatMap(([, rows]) => rows));
        for (const pr of parsedRows) {
          if (dupCoordRows.has(pr.row)) {
            if (!pr.errorFields.includes('lat')) pr.errorFields.push('lat');
            if (!pr.errorFields.includes('lon')) pr.errorFields.push('lon');
            pr.hasError = true;
          }
        }
      }
      if (detectedClosePairs.length > 0) {
        const closeRows = new Set(
          detectedClosePairs.flatMap((pair) => [pair.earlierRow, pair.laterRow])
        );
        for (const pr of parsedRows) {
          if (closeRows.has(pr.row)) {
            if (!pr.errorFields.includes('lat')) pr.errorFields.push('lat');
            if (!pr.errorFields.includes('lon')) pr.errorFields.push('lon');
            pr.hasError = true;
          }
        }
      }

      // Check for invalid Description values
      if (badDescRows.length > 0) {
        const shown = badDescRows.slice(0, 5);
        const msgs = shown.map((r) => `"${r.value}" on row ${r.row}`);
        const extra =
          badDescRows.length > 5 ? ` and ${badDescRows.length - 5} more` : '';
        errors.push(`Invalid Description: ${msgs.join('; ')}${extra}`);
      }

      // Check first row description is LAUNCH, last is RECEIVE
      if (descriptions.length > 0) {
        if (descriptions[0].value !== 'LAUNCH') {
          errors.push('First data row Description must be "LAUNCH".');
        }
        if (descriptions[descriptions.length - 1].value !== 'RECEIVE') {
          errors.push('Last data row Description must be "RECEIVE".');
        }
        const launchCount = descriptions.filter(
          (d) => d.value === 'LAUNCH'
        ).length;
        const receiveCount = descriptions.filter(
          (d) => d.value === 'RECEIVE'
        ).length;
        if (launchCount > 1) {
          errors.push(
            `"LAUNCH" must appear exactly once but was found ${launchCount} times.`
          );
        }
        if (receiveCount > 1) {
          errors.push(
            `"RECEIVE" must appear exactly once but was found ${receiveCount} times.`
          );
        }

        // Check LAUNCH/RECEIVE do not appear in middle rows
        const middleDescs = descriptions.slice(1, -1);
        const middleLaunch = middleDescs
          .filter((d) => d.value === 'LAUNCH')
          .map((d) => d.row);
        const middleReceive = middleDescs
          .filter((d) => d.value === 'RECEIVE')
          .map((d) => d.row);
        if (middleLaunch.length > 0) {
          errors.push(
            `"LAUNCH" must only appear in the first row but was also found on row${
              middleLaunch.length > 1 ? 's' : ''
            } ${middleLaunch.join(', ')}.`
          );
        }
        if (middleReceive.length > 0) {
          errors.push(
            `"RECEIVE" must only appear in the last row but was also found on row${
              middleReceive.length > 1 ? 's' : ''
            } ${middleReceive.join(', ')}.`
          );
        }
      }

      if (markers.length > 0) {
        firstMarker = markers[0];
        lastMarker = markers[markers.length - 1];
      }

      return {
        valid: errors.length === 0,
        errors,
        warnings,
        markerCount,
        firstMarker,
        lastMarker,
        skipCount,
        hasDistance,
        distanceUnit,
        closeMarkerPairs,
        duplicateMarkerNameChanges,
        parsedRows,
      };
    },

    createDiameterRule(val: string): boolean | string {
      if (!val || val.trim().length === 0) return true;
      const num = parseFloat(val);
      if (isNaN(num) || num <= 0) return 'Diameter must be a positive number.';
      const meters =
        this.createDiameterUnit === 'in' ? num * 0.0254 : num * 0.01;
      if (meters < 0.1016 || meters > 1.27) {
        return this.createDiameterUnit === 'in'
          ? 'Diameter must be between 4 and 50 inches.'
          : 'Diameter must be between 10.16 and 127 cm.';
      }
      return true;
    },

    resetForm() {
      this.tab = 'create';
      this.createName = '';
      this.createNameError = '';
      this.createDiameter = '';
      this.createDiameterError = '';
      this.createDiameterUnit = 'in';
      this.createCsvFile = null;
      this.createCsvFileError = '';
      this.createCsvValidation = null;
      this.createCsvMode = null;
      this.showBuilderDialog = false;
      this.builderOperation = 'create';
      this.resetBuilderState();
      this.selectedPipelineSection = null;
      this.updateDiameter = '';
      this.updateDiameterUnit = 'in';
      this.updateCsvFile = null;
      this.updateCsvMode = null;
      this.updateAction = null;
      this.updateCsvValidation = null;
      this.currentTrackingFileName = '';
      this.currentTrackingFile = '';
      this.viewTrackingFileDialog = false;
    },

    loadPipelineSections() {
      this.loading = true;

      PipelineSectionService.getPipelineSections()
        .then((response: any) => {
          this.pipelineSections = response.data.map(
            (ps: any) =>
              new PipelineSection(
                ps.pipelineSectionId,
                ps.name,
                ps.diameter,
                ps.trackingFileName,
                ps.trackingFile
              )
          );
          this.pipelineSectionOptions = this.pipelineSections
            .map((ps: PipelineSection) => ({
              label: ps.name,
              value: ps.pipelineSectionId,
            }))
            .sort((a: any, b: any) => a.label.localeCompare(b.label));
        })
        .catch((err: any) => {
          console.error('Failed to load pipeline sections:', err);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    onPipelineSectionSelected(val: any) {
      if (!val) {
        return;
      }
      this.onResetUpdateCsvMode();
      this.resetBuilderState();
      this.showBuilderDialog = false;
      const section = this.pipelineSections.find(
        (ps) => ps.pipelineSectionId === val.value
      );
      if (section) {
        if (section.diameter != null) {
          const converted =
            this.updateDiameterUnit === 'in'
              ? section.diameter / 0.0254
              : section.diameter / 0.01;
          this.updateDiameter = String(parseFloat(converted.toFixed(4)));
        } else {
          this.updateDiameter = '';
        }
        this.currentTrackingFileName = section.trackingFileName || '';
        this.currentTrackingFile = section.trackingFile || '';
        this.$nextTick(() => {
          this.plotCsvPointsOnMiniMap(this.currentTrackingFile);
        });
      }
    },

    onPipelineSectionCleared() {
      this.updateDiameter = '';
      this.updateDiameterUnit = 'in';
      this.onResetUpdateCsvMode();
      this.resetBuilderState();
      this.showBuilderDialog = false;
      this.currentTrackingFileName = '';
      this.currentTrackingFile = '';
      this.clearMiniMap();
    },

    onViewTrackingFile() {
      if (this.currentTrackingFile) {
        this.viewTrackingFileDialog = true;
        this.plotCsvPointsOnMap(this.currentTrackingFile);
      }
    },

    onViewTrackingFileDialogHide() {
      this.clearCsvMapMarkers();
    },

    plotCsvPointsOnMap(csv: string) {
      this.clearCsvMapMarkers();
      if (!this.map || !csv) return;

      const lines = csv
        .split('\n')
        .map((l: string) => l.trim())
        .filter((l: string) => l.length > 0);
      if (lines.length < 2) return;

      const header = lines[0]
        .split(',')
        .map((h: string) => h.trim().toLowerCase());
      const latIdx = header.indexOf('latitude');
      const lonIdx = header.indexOf('longitude');
      const nameIdx = header.indexOf('agm marker name');
      if (latIdx === -1 || lonIdx === -1) return;

      const latLngs: L.LatLng[] = [];

      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(',').map((c: string) => c.trim());
        const lat = parseFloat(cols[latIdx]);
        const lon = parseFloat(cols[lonIdx]);
        if (isNaN(lat) || isNaN(lon)) continue;

        const name = nameIdx !== -1 ? cols[nameIdx] : `Point ${i}`;
        const latlng = L.latLng(lat, lon);
        latLngs.push(latlng);

        const marker = L.marker(latlng).bindTooltip(name).addTo(this.map);
        this.csvMapMarkers.push(markRaw(marker));
      }

      if (latLngs.length > 1) {
        this.csvPolyline = markRaw(
          L.polyline(latLngs, {
            color: '#1976D2',
            weight: 2,
            dashArray: '6 4',
          }).addTo(this.map)
        );
        this.map.fitBounds(this.csvPolyline.getBounds());
      } else if (latLngs.length === 1) {
        this.map.setView(latLngs[0], 14);
      }
    },

    clearCsvMapMarkers() {
      for (const marker of this.csvMapMarkers) {
        marker.removeFrom(this.map);
      }
      this.csvMapMarkers = [];
      if (this.csvPolyline) {
        this.csvPolyline.removeFrom(this.map);
        this.csvPolyline = null;
      }
    },

    initMiniMap() {
      if (this.miniMap) return;
      const el = this.$refs.miniMap as HTMLElement;
      if (!el) return;

      this.miniMap = markRaw(
        L.map(el, {
          zoomControl: true,
          attributionControl: false,
        }).setView([0, 0], 2)
      );

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
      }).addTo(this.miniMap as L.Map);

      this.miniMapLayer = markRaw(L.layerGroup().addTo(this.miniMap as L.Map));
    },

    clearMiniMap() {
      if (this.miniMapLayer) {
        this.miniMapLayer.clearLayers();
      }
    },

    destroyMiniMap() {
      if (this.miniMap) {
        this.miniMap.remove();
        this.miniMap = null;
        this.miniMapLayer = null;
      }
    },

    initCreateMiniMap() {
      if (this.createMiniMap) return;
      const el = this.$refs.createMiniMap as HTMLElement;
      if (!el) return;

      this.createMiniMap = markRaw(
        L.map(el, {
          zoomControl: true,
          attributionControl: false,
        }).setView([0, 0], 2)
      );

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
      }).addTo(this.createMiniMap as L.Map);

      this.createMiniMapLayer = markRaw(
        L.layerGroup().addTo(this.createMiniMap as L.Map)
      );
    },

    clearCreateMiniMap() {
      if (this.createMiniMapLayer) {
        this.createMiniMapLayer.clearLayers();
      }
    },

    destroyCreateMiniMap() {
      if (this.createMiniMap) {
        this.createMiniMap.remove();
        this.createMiniMap = null;
        this.createMiniMapLayer = null;
      }
    },

    initBuilderMiniMap() {
      if (this.builderMiniMap) return;
      const el = this.$refs.builderMiniMap as HTMLElement;
      if (!el) return;

      this.builderMiniMap = markRaw(
        L.map(el, {
          zoomControl: true,
          attributionControl: false,
        }).setView([0, 0], 2)
      );

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
      }).addTo(this.builderMiniMap as L.Map);

      this.builderMiniMapLayer = markRaw(
        L.layerGroup().addTo(this.builderMiniMap as L.Map)
      );
    },

    clearBuilderMiniMap() {
      if (this.builderMiniMapLayer) {
        this.builderMiniMapLayer.clearLayers();
      }
      this.builderMiniMapMarkers = [];
    },

    destroyBuilderMiniMap() {
      if (this.builderMiniMap) {
        this.builderMiniMap.remove();
        this.builderMiniMap = null;
        this.builderMiniMapLayer = null;
      }
      this.builderMiniMapMarkers = [];
    },

    getBuilderTrackIcon(trackState: 'T' | 'S'): L.Icon | L.DivIcon {
      return trackState === 'S'
        ? PigRunCommon.skipIcon
        : PigRunCommon.trackIcon_2;
    },

    getBuilderMarkerTooltip(index: number): string {
      const name = this.builderMarkerNames[index];
      const state = this.builderTrackStates[index] === 'T' ? 'TRACK' : 'SKIP';
      return `${name} (${state})`;
    },

    onBuilderMiniMapMarkerClick(index: number) {
      const currentState = this.builderTrackStates[index];
      if (currentState !== 'T' && currentState !== 'S') {
        return;
      }

      const nextState: 'T' | 'S' = currentState === 'T' ? 'S' : 'T';
      this.builderTrackStates[index] = nextState;

      const marker = this.builderMiniMapMarkers[index];
      if (!marker) return;

      marker.setIcon(this.getBuilderTrackIcon(nextState));
      marker.setTooltipContent(this.getBuilderMarkerTooltip(index));
    },

    plotBuilderCoordinatesOnMiniMap() {
      this.initBuilderMiniMap();
      this.clearBuilderMiniMap();
      if (!this.builderMiniMap || !this.builderMiniMapLayer) return;

      const latitudes = this.getBuilderColumnValues(this.builderLatitudesRaw);
      const longitudes = this.getBuilderColumnValues(this.builderLongitudesRaw);
      const latLngs: L.LatLng[] = [];
      const markers: (L.Marker | null)[] = [];

      for (let i = 0; i < this.builderMarkerNames.length; i++) {
        const lat = parseFloat(latitudes[i]);
        const lon = parseFloat(longitudes[i]);
        if (isNaN(lat) || isNaN(lon)) {
          markers.push(null);
          continue;
        }

        const latlng = L.latLng(lat, lon);
        latLngs.push(latlng);

        const trackState = this.builderTrackStates[i] === 'S' ? 'S' : 'T';
        const marker = L.marker(latlng, {
          icon: this.getBuilderTrackIcon(trackState),
        })
          .bindTooltip(this.getBuilderMarkerTooltip(i), {
            permanent: false,
            direction: 'top',
            offset: [0, -8],
          })
          .addTo(this.builderMiniMapLayer as L.LayerGroup);

        marker.on('click', () => {
          this.onBuilderMiniMapMarkerClick(i);
        });

        markers.push(markRaw(marker));
      }

      this.builderMiniMapMarkers = markers;

      if (latLngs.length > 1) {
        L.polyline(latLngs, {
          color: '#1976D2',
          weight: 2,
          dashArray: '6 4',
        }).addTo(this.builderMiniMapLayer as L.LayerGroup);
      }

      setTimeout(() => {
        if (!this.builderMiniMap) return;
        this.builderMiniMap.invalidateSize();
        if (latLngs.length > 1) {
          this.builderMiniMap.fitBounds(L.latLngBounds(latLngs), {
            padding: [20, 20],
          });
        } else if (latLngs.length === 1) {
          this.builderMiniMap.setView(latLngs[0], 14);
        }
      }, 100);
    },

    plotCsvPointsOnCreateMiniMap(csv: string) {
      this.initCreateMiniMap();
      this.clearCreateMiniMap();
      if (!this.createMiniMap || !this.createMiniMapLayer || !csv) return;

      this.plotCsvPointsOnLayer(
        csv,
        this.createMiniMap as L.Map,
        this.createMiniMapLayer as L.LayerGroup
      );
    },

    plotCsvPointsOnMiniMap(csv: string) {
      this.initMiniMap();
      this.clearMiniMap();
      if (!this.miniMap || !this.miniMapLayer || !csv) return;

      this.plotCsvPointsOnLayer(
        csv,
        this.miniMap as L.Map,
        this.miniMapLayer as L.LayerGroup
      );
    },

    plotCsvPointsOnLayer(csv: string, map: L.Map, layer: L.LayerGroup) {
      const lines = csv
        .split('\n')
        .map((l: string) => l.trim())
        .filter((l: string) => l.length > 0);
      if (lines.length < 2) return;

      const header = lines[0]
        .split(',')
        .map((h: string) => h.trim().toLowerCase());
      const latIdx = header.indexOf('latitude');
      const lonIdx = header.indexOf('longitude');
      const nameIdx = header.indexOf('agm marker name');
      const stateIdx = header.indexOf('state');
      if (latIdx === -1 || lonIdx === -1) return;

      const latLngs: L.LatLng[] = [];

      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(',').map((c: string) => c.trim());
        const lat = parseFloat(cols[latIdx]);
        const lon = parseFloat(cols[lonIdx]);
        if (isNaN(lat) || isNaN(lon)) continue;

        const name = nameIdx !== -1 ? cols[nameIdx] : `Point ${i}`;
        const state =
          stateIdx !== -1 ? (cols[stateIdx] || '').toUpperCase() : '';
        const trackState: 'T' | 'S' = state === 'SKIP' ? 'S' : 'T';
        const stateLabel =
          state === 'TRACK' || state === 'SKIP' ? state : 'TRACK';
        const latlng = L.latLng(lat, lon);
        latLngs.push(latlng);

        L.marker(latlng, {
          icon: this.getBuilderTrackIcon(trackState),
        })
          .bindTooltip(`${name} (${stateLabel})`, {
            permanent: false,
            direction: 'top',
            offset: [0, -8],
          })
          .addTo(layer);
      }

      if (latLngs.length > 1) {
        L.polyline(latLngs, {
          color: '#1976D2',
          weight: 2,
          dashArray: '6 4',
        }).addTo(layer);
      }

      setTimeout(() => {
        map.invalidateSize();
        if (latLngs.length > 1) {
          map.fitBounds(L.latLngBounds(latLngs), {
            padding: [20, 20],
          });
        } else if (latLngs.length === 1) {
          map.setView(latLngs[0], 14);
        }
      }, 100);
    },

    onDownloadTrackingFile() {
      if (!this.currentTrackingFile || !this.currentTrackingFileName) {
        return;
      }
      const blob = new Blob([this.currentTrackingFile], {
        type: 'text/csv;charset=utf-8;',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', this.currentTrackingFileName);
      link.click();
      URL.revokeObjectURL(url);
    },

    onCreateSubmit() {
      this.validateCreateNameAndDiameter();
      this.createCsvFileError = '';

      const file = this.createCsvFile;
      if (!file) {
        this.createCsvFileError = 'CSV file is required.';
      } else if (!file.name || file.name.trim().length === 0) {
        this.createCsvFileError = 'File name must not be empty.';
      } else if (file.name.trim().length > 500) {
        this.createCsvFileError = 'File name must be 500 characters or less.';
      } else if (file.size === 0) {
        this.createCsvFileError = 'File must not be empty.';
      }

      if (
        this.createNameError ||
        this.createDiameterError ||
        this.createCsvFileError
      ) {
        return;
      }

      if (this.createCsvValidation && !this.createCsvValidation.valid) {
        return;
      }

      this.loading = true;

      const reader = new FileReader();
      reader.onload = (e) => {
        const fileContent = e.target?.result as string;
        const validation = this.validateCsv(fileContent);
        this.createCsvValidation = validation;
        if (!validation.valid) {
          this.loading = false;
          return;
        }
        const request = this.createPipelineRequest(
          this.createCsvFile!.name.trim(),
          fileContent
        );
        console.log('CreatePipelineRequest (CSV path):', request);
        PipelineSectionService.createPipelineSection(request)
          .then(() => {
            this.$q.notify({
              color: 'positive',
              message: 'Pipeline created.',
            });
            this.$emit('update:modelValue', false);
            this.refreshAndEmit();
          })
          .catch((error: any) => {
            console.error('Failed to create pipeline:', error);
            this.$q.notify({
              color: 'negative',
              message: 'Failed to create pipeline.',
            });
          })
          .finally(() => {
            this.loading = false;
          });
      };
      reader.readAsText(this.createCsvFile!);
    },

    onUpdateSubmit() {
      if (this.updateCsvMode === 'builder') {
        return;
      }

      if (!this.selectedPipelineSection || !this.updateDiameter) {
        this.$q.notify({
          color: 'warning',
          message: 'Name and Diameter are required.',
        });
        return;
      }

      if (
        this.updateCsvFile &&
        this.updateCsvValidation &&
        !this.updateCsvValidation.valid
      ) {
        this.$q.notify({
          color: 'warning',
          message: 'Please fix CSV validation errors before updating.',
        });
        return;
      }

      if (!this.validateUpdateDiameter()) {
        return;
      }

      this.loading = true;
      this.updateAction = 'update';

      const sendUpdate = (
        trackingFileName: string | null,
        trackingFile: string | null
      ) => {
        const request = new UpdatePipelineRequest(
          this.selectedPipelineSection.value,
          this.getUpdateDiameterMeters(),
          trackingFileName,
          trackingFile
        );
        PipelineSectionService.updatePipelineSection(request)
          .then(() => {
            this.$q.notify({
              color: 'positive',
              message: 'Pipeline updated.',
            });
            this.$emit('update:modelValue', false);
            this.refreshAndEmit();
          })
          .catch((error: any) => {
            console.error('Failed to update pipeline:', error);
            this.$q.notify({
              color: 'negative',
              message: 'Failed to update pipeline.',
            });
          })
          .finally(() => {
            this.updateAction = null;
            this.loading = false;
          });
      };

      if (this.updateCsvFile) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const fileContent = e.target?.result as string;
          const validation = this.validateCsv(fileContent);
          this.updateCsvValidation = validation;
          if (!validation.valid) {
            this.updateAction = null;
            this.loading = false;
            return;
          }
          sendUpdate(this.updateCsvFile!.name.trim(), fileContent);
        };
        reader.readAsText(this.updateCsvFile);
      } else {
        sendUpdate(null, null);
      }
    },

    onDeleteSubmit() {
      if (!this.selectedPipelineSection) {
        this.$q.notify({
          color: 'warning',
          message: 'Please select a pipeline section to delete.',
        });
        return;
      }

      this.$q
        .dialog({
          title: 'Confirm Delete',
          message: `Are you sure you want to delete "${this.selectedPipelineSection.label}"? This action cannot be undone.`,
          cancel: true,
          persistent: true,
        })
        .onOk(() => {
          this.updateAction = 'delete';
          this.loading = true;
          PipelineSectionService.deletePipelineSection(
            this.selectedPipelineSection.value
          )
            .then(() => {
              this.$q.notify({
                color: 'positive',
                message: 'Pipeline deleted.',
              });
              this.selectedPipelineSection = null;
              this.updateDiameter = '';
              this.currentTrackingFileName = '';
              this.currentTrackingFile = '';
              this.refreshAndEmit();
            })
            .catch((error: any) => {
              console.error('Failed to delete pipeline:', error);
              this.$q.notify({
                color: 'negative',
                message: 'Failed to delete pipeline.',
              });
            })
            .finally(() => {
              this.updateAction = null;
              this.loading = false;
            });
        });
    },

    refreshAndEmit() {
      PipelineSectionService.getPipelineSections()
        .then((response: any) => {
          this.pipelineSections = response.data.map(
            (ps: any) =>
              new PipelineSection(
                ps.pipelineSectionId,
                ps.name,
                ps.diameter,
                ps.trackingFileName,
                ps.trackingFile
              )
          );
          const options = this.pipelineSections
            .map((ps: PipelineSection) => ({
              label: ps.name,
              value: ps.pipelineSectionId,
            }))
            .sort((a: any, b: any) => a.label.localeCompare(b.label));
          this.pipelineSectionOptions = options;
          this.$emit('sections-updated', options);
        })
        .catch((err: any) => {
          console.error('Failed to refresh pipeline sections:', err);
        });
    },
  },
});
</script>

<style scoped>
.manage-ps-dialog .q-tab :deep(.q-tab__label) {
  text-transform: none;
}

.csv-drop-zone {
  border: 2px dashed transparent;
  border-radius: 6px;
  padding: 4px;
  transition: border-color 0.2s, background-color 0.2s;
}

.csv-drop-zone--active {
  border-color: #1976d2;
  background-color: rgba(25, 118, 210, 0.05);
}

.csv-preview-table {
  font-size: 12px;
  overflow-x: auto;
}

.csv-preview-table :deep(td) {
  white-space: nowrap;
}

.csv-preview-table :deep(thead th) {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #fff;
}

.csv-preview-table :deep(.q-table__top),
.csv-preview-table :deep(.q-table__bottom) {
  display: none;
}

.csv-preview-table :deep(tr.highlight-flash) {
  animation: flash-row 1.5s ease-out;
}

@keyframes flash-row {
  0% {
    background-color: #ffeb3b;
  }
  100% {
    background-color: transparent;
  }
}
</style>
